"use client";

import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useSyncExternalStore,
  useRef,
} from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { getQueryClient } from "~/widgets/lib/query-client";
import { getSourceInfo } from "~/widgets/lib/getSourceInfo";
import { useSchoolCheckoutConfig } from "../PurchaseOverlay/useSchoolCheckoutConfig";
import { basketCheckoutGate } from "./checkoutGate";
import { basketElementFor, requestBasketDrawer } from "~/widgets/lib/ensure-basket-element";
import { countBasketItems } from "@baluu/ui/lib/checkout/basket-surface";
import {
  bookingFeeDetailText,
  estimateBookingFeePence,
  readBookingFeeDisclosure,
  resolveDisplayedBookingFee,
} from "@baluu/ui/lib/checkout/booking-fee-disclosure";
import { Button } from "@baluu/ui/components/button";
import { trackConversion } from "~/widgets/lib/tracking";
import { basketStore } from "~/widgets/stores/basket-store";
import type {
  BasketItem,
  BasketValidationResult,
} from "~/widgets/stores/basket-store";
import {
  validateBasketItems,
  countBlockingItems,
  identityBlockers,
  apiOrigin,
} from "~/widgets/lib/basket-validate";
import { defaultUnavailabilityMessage } from "@obby/lib";
import {
  formatBookingWindow,
  resolveDisplayTimezone,
  resolveRowTimezone,
  productSurcharge,
  productSurchargeLines,
} from "@baluu/ui/lib/checkout";
import { FinalStep } from "@baluu/ui/components/checkout/final-step";
import type { OrderItem } from "@baluu/ui/components/checkout/final-step";
import { PersonalDetailsForm } from "@baluu/ui/components/checkout/personal-details-form";
import type { PersonalDetailsValues } from "@baluu/ui/components/checkout/personal-details-form";
import { MandatoryAcceptanceCheckbox } from "@baluu/ui/components/checkout/mandatory-acceptance-checkbox";
import {
  DeliveryAddressForm,
  EMPTY_DELIVERY_ADDRESS,
  isDeliveryAddressComplete,
} from "@baluu/ui/components/checkout/delivery-address-form";
import type { DeliveryAddressValues } from "@baluu/ui/components/checkout/delivery-address-form";
import {
  validateEmail,
  validatePhone,
  shouldEnforceBuyerPhone,
  CheckoutLoginGate,
} from "@baluu/ui/components/checkout";
import { CheckoutCustomerFields } from "@baluu/ui/components/checkout/customer-fields";
import {
  CUSTOMER_FIELDS_LOADING_ERROR,
  customerFieldAnswersForPayload,
  formatLinkedCustomerFieldValue,
  validateCustomerFieldAnswers,
} from "@baluu/ui/lib/checkout/customer-field-answers";
import { CurrencyISO__CurrencySymbol } from "@obby/lib";
import { useBasketCustomerFields } from "./useBasketCustomerFields";
import { useBasketPaymentOptions } from "./useBasketPaymentOptions";
import { useBasketLoyalty } from "./useBasketLoyalty";
import { useBasketHolds } from "./useBasketHolds";
import { DiscountCodesSection } from "@baluu/ui/components/checkout/discount-codes-section";
import { LoyaltyCheckoutSection } from "@baluu/ui/components/checkout/loyalty-checkout-section";
import {
  computeDiscountShares,
  discountCodeAddError,
  discountCodesForPayload,
  discountValidateBody,
  isGiftCardDiscount,
  singleCodeDiscountPercentage,
  validatedDiscountError,
  type AppliedDiscount,
} from "@baluu/ui/lib/checkout/discount-codes";
import {
  paymentOptionError,
  redemptionConfirmBody,
  redemptionTotalLabel,
  resolveRedemption,
  type CheckoutPaymentOption,
} from "@baluu/ui/lib/checkout/payment-options";
import { redeemPointsForPayload } from "@baluu/ui/lib/checkout/loyalty";
import {
  basketPaymentReturnUrl,
  forgetBasketPaymentReturn,
  markReturnHandled,
  rememberBasketPaymentReturn,
  verifyPaymentReturn,
  type BasketPaymentReturnProp,
} from "~/widgets/lib/payment-return";
// Inline formatPrice to avoid TS resolution issue with @baluu/ui subpath exports
function formatPrice(price: number, currency = "GBP"): string {
  const symbols: Record<string, string> = {
    GBP: "\u00A3",
    USD: "$",
    EUR: "\u20AC",
    NZD: "NZ$",
    AUD: "A$",
    CAD: "CA$",
    CHF: "CHF",
    SEK: "kr",
    NOK: "kr",
    DKK: "kr",
    PLN: "z\u0142",
    JPY: "\u00A5",
    SGD: "S$",
    HKD: "HK$",
    INR: "\u20B9",
    ZAR: "R",
    BRL: "R$",
    MXN: "MX$",
    AED: "AED",
    THB: "\u0E3F",
  };
  const sym = symbols[currency] || currency + " ";
  const formatted = (price / 100).toFixed(2);
  return `${sym}${formatted}`;
}
import { StripePaymentPortal } from "../StripePaymentPortal";
import { SuccessScreenPortal } from "../SuccessScreenPortal";
import { TermsDialogPortal } from "../TermsDialogPortal";
import { CustomerAuthDialog } from "../CustomerAuthDialog/CustomerAuthDialog";
import { useAuthSnapshot, hydrateSession } from "~/widgets/lib/auth-client";
import type { SuccessData } from "../PortalManager";
import type { StripePaymentData } from "../CheckoutFlow/CheckoutFlow";
import type {
  TermsSettings,
  CustomTerms,
} from "@baluu/ui/components/checkout/utils/types";
import {
  ShoppingCart,
  ShoppingBag,
  CreditCard,
  ExternalLink,
  AlertCircle,
  X,
  Calendar,
  Tag,
  Lock,
  User,
  CheckCircle,
  Mail,
  Loader2,
  ChevronDown,
  Package,
  Send,
} from "lucide-react";
import {
  classifyRequestToBookBasket,
  describeRequestNoPayment,
  describeRequestSent,
  describeDepositAtRequest,
  describeDepositRequestSent,
  isDepositAtRequestBasket,
  isUnpaidRequestResponse,
  isDepositRequestResponse,
  REQUEST_SENT_TITLE,
  requestToBookItemsKey,
  requestToBookTotalLabel,
  resolveIsRequestToBook,
  type ServerRequestToBookAnswer,
} from "@baluu/ui/lib/checkout/request-to-book";
import {
  confirmBasketOrder,
  dispatchBookingRequested,
  resolveOverlaySessionOutcome,
} from "./basketSessionOutcome";
import { BasketApiError } from "@baluu/ui/lib/checkout/basket-api";
import {
  contentsRefusalFor,
  contentsRefusalSpent,
  REMOVE_FROM_SUMMARY_HINT,
  type ContentsRefusal,
} from "@baluu/ui/lib/checkout/basket-contents-refusal";
import { errorSlotFor } from "./contentsRefusal";

const DEFAULT_API_URL = "https://api.baluu.io";

export type BasketCheckoutOverlayProps = {
  businessId: string;
  apiUrl?: string;
  primaryColor?: string;
  gtagConversionId?: string;
  gtagConversionLabel?: string;
  /** Opened by the bundle on the way back from a Stripe redirect taken at
   *  the card step (lib/payment-return): finish that order. */
  paymentReturn?: BasketPaymentReturnProp;
};

type SchoolConfig = {
  name: string;
  slug: string;
  timezone: string;
  preferredCurrency: string;
  /** The school's preference; see `paymentMode` below for why it isn't read. */
  paymentMode: "embedded" | "hosted";
  stripePublicKey: string;
  termsSettings: TermsSettings;
  customTerms?: CustomTerms;
  cancellationPolicyUrl: string;
  businessCountry?: string;
  isNewsletterSignupEnabled?: boolean;
  customNewsletterSignupText?: string | null;
  /** School setting: require the customer to sign in before checkout.
   *  Optional so a response missing the key (older cache, partial payload)
   *  never breaks — absent is treated as "don't gate", same as `false`. */
  requireLoginToBook?: boolean;
  /** The school's booking fee, for the first-paint note (R26). Read through
   *  readBookingFeeDisclosure, never trusted as shaped. */
  bookingFeeDisclosure?: unknown;
  /** The school's basket settings; `allowDiscountStacking` lets an order
   *  carry several codes (the server enforces it too), and
   *  `reservationsEnabled` holds the seats while the buyer checks out. */
  basket?: { allowDiscountStacking?: boolean; reservationsEnabled?: boolean };
};

type BuyerDetails = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  marketingOptIn: boolean;
};

// Re-validate after the buyer pauses typing a valid-looking email so the
// server can exclude THIS buyer's own holds from the "others" sum. Without
// it, a buyer who reloads after a failed Stripe attempt sees a false "no
// longer available" until the cron sweeper clears their own reservations.
const VALIDATE_DEBOUNCE_MS = 500;

/** Back from Stripe with the payment not taken (a declined 3-D Secure
 *  check, a bank redirect abandoned): the basket is as it was. */
const PAYMENT_RETURN_FAILED =
  "Your payment wasn't completed, so nothing was charged. Please try again, or choose another payment method.";

/**
 * The Pay-time refusals, named so the effect below can retract exactly these
 * and nothing else — never a Stripe or form error.
 *
 * Three of them because the remedy differs, and pointing at a basket that is
 * not on the page is worse than saying nothing: `ensureBasketElement` puts
 * no `<baluu-basket>` on a surface with the basket off (every embed but
 * Website v2), so the surfaces that open this overlay there have no drawer
 * to open.
 */
const BLOCKED_ITEMS_ERROR =
  "Open your basket to fix the flagged items before continuing to checkout.";
const BLOCKED_ITEMS_ERROR_NO_DRAWER =
  "We can't take payment for this basket as it stands.";
// Split by kind for the same reason `identityBlockCopy` is: the remedies
// differ, and a single string had the banner telling a member to use their
// membership address while the error directly beneath it told them to look
// for a typo.
//
// Neither mentions removing the item, unlike the banner. The remedy they name
// — the email field — is always on this surface; a basket to remove from is
// not (on a surface with the basket off, `ensureBasketElement` puts none on
// the page, so the overlay is opened with no drawer at all).
const BLOCKED_ITEMS_ERROR_IDENTITY_MEMBERS =
  "Use the email address on your membership above.";
const BLOCKED_ITEMS_ERROR_IDENTITY_RESTRICTED =
  "Check the email address above — some items are restricted to selected customers.";
// The trial allowance is the identity verdict whose remedy is NOT a different
// email — the address is right, the allowance is what's short — so this one
// names the only move that clears it. Worded to be true for every state the
// code covers (already used, partly used, or simply too many in one basket):
// these strings are matched literally by the retraction effect below, so
// they cannot be derived per item the way the banner copy is.
const BLOCKED_ITEMS_ERROR_IDENTITY_TRIAL =
  "Your trial allowance for this class doesn't cover this basket — remove the trial ticket, or book a regular ticket instead.";
// Same verdict, but on a host with no <baluu-basket>: "Review basket" is
// withheld there, so naming the drawer would be an instruction the buyer
// cannot follow.
const BLOCKED_ITEMS_ERROR_IDENTITY_TRIAL_NO_DRAWER =
  "Your trial allowance for this class doesn't cover this basket, and we can't take payment for it as it stands — please contact the school.";
// A trial over the allowance alongside a members-only / restricted item:
// both remedies apply, and neither alone clears the basket.
const BLOCKED_ITEMS_ERROR_IDENTITY_MIXED_TRIAL =
  "Check the email address above for the restricted items, and remove the trial ticket that is over your allowance from your basket.";
const BLOCKED_ITEMS_ERROR_CHECKING =
  "Still checking availability — try again in a moment.";
const BLOCKED_ITEMS_ERRORS = new Set([
  BLOCKED_ITEMS_ERROR,
  BLOCKED_ITEMS_ERROR_NO_DRAWER,
  BLOCKED_ITEMS_ERROR_IDENTITY_MEMBERS,
  BLOCKED_ITEMS_ERROR_IDENTITY_RESTRICTED,
  BLOCKED_ITEMS_ERROR_IDENTITY_TRIAL,
  BLOCKED_ITEMS_ERROR_IDENTITY_TRIAL_NO_DRAWER,
  BLOCKED_ITEMS_ERROR_IDENTITY_MIXED_TRIAL,
  BLOCKED_ITEMS_ERROR_CHECKING,
]);

type CollapsibleSectionProps = {
  title: string;
  icon: React.ReactNode;
  badge?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
};

function CollapsibleSection({
  title,
  icon,
  badge,
  defaultOpen = false,
  children,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 px-5 py-3.5 text-left"
      >
        {icon}
        <span className="font-semibold text-foreground text-sm uppercase tracking-wide flex-1">
          {title}
        </span>
        {badge}
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"}`}
      >
        <div className="border-t border-border">{children}</div>
      </div>
    </div>
  );
}

// A product item that ships: the mount-time validate stamps the server's
// postage-configured answer; postageId is the fallback when that call
// didn't run (offline / race) so we never under-collect.
function itemNeedsDelivery(item: BasketItem) {
  return (
    item.type === "product" &&
    Boolean(item.requiresDeliveryAddress || item.postageId)
  );
}

function getItemDateDescription(
  item: BasketItem,
  timezone: string,
): string | undefined {
  if (item.type !== "booking") return undefined;
  try {
    // ⚠ CURRENTLY UNREACHABLE. `termData` is declared on the item type and
    // forwarded to the API, but no producer anywhere writes it — `CheckoutTerm`
    // sets `termName` and `sessionDate`, so a real term row falls through to
    // the start branch below. Kept because a basket cached by an older build
    // could still carry it, and because it is correct if it ever does; do not
    // read the zone handling here as load-bearing.
    if (item.termData) {
      // ⚠ On the LISTING's clock, like the window below. A term startDate
      // stored at midnight UTC names the PREVIOUS day for any buyer west of
      // UTC, and a term row sitting beside a session row from the same school
      // was dated on a different clock than it. Intl throws RangeError on an
      // unknown IANA name where moment merely logs, so an unusable zone falls
      // back to no zone rather than dropping the whole line.
      const termDay = (iso: string) => {
        const opts: Intl.DateTimeFormatOptions = {
          day: "numeric",
          month: "short",
          year: "numeric",
        };
        const fmt = (zone?: string) =>
          new Date(iso).toLocaleDateString("en-GB", {
            ...opts,
            ...(zone ? { timeZone: zone } : {}),
          });
        // ⚠ Through `resolveRowTimezone`, exactly like the session branch
        // below. Validating with Intl alone accepts names the bundled
        // moment-timezone data does not have (a recent tzdata addition such as
        // America/Ciudad_Juarez), so a term row would render in that zone
        // while an appointment row from the same school fell back — two rows,
        // one screen, different clocks.
        const zone = resolveRowTimezone(item, timezone);
        try {
          return fmt(zone);
        } catch {
          return fmt(undefined);
        }
      };
      const start = termDay(item.termData.startDate);
      const end = termDay(item.termData.endDate);
      return `${start} – ${end}`;
    }
    // Date + the window actually being booked, on the LISTING's clock. The
    // timezone was passed in and discarded (`_timezone`), so this rendered in
    // the browser's zone and showed only the start.
    const startIso = item.appointmentData?.startTime || item.sessionDate;
    if (startIso) {
      // Intl rather than moment: this app has no moment dependency, and a
      // native formatter with an explicit timeZone is exactly as correct.
      // ⚠ Intl throws RangeError on an unknown IANA zone, and this whole
      // function sits in a try/catch that returns undefined — so one bad
      // `item.timezone` erased the date AND the time from the final checkout
      // screen. The old toLocaleDateString call passed no timeZone and could
      // not throw. formatBookingWindow does not throw on the same input
      // (moment falls back), so without this the two halves failed differently.
      const dayFmt = (zone?: string) =>
        new Intl.DateTimeFormat("en-GB", {
          weekday: "short",
          day: "numeric",
          month: "short",
          ...(zone ? { timeZone: zone } : {}),
        }).format(new Date(startIso));
      // ⚠ Fall back to the SCHOOL's zone, not to none. Intl throws RangeError
      // on an unknown IANA name while moment.tz does NOT — it logs and leaves
      // the moment in UTC — so falling back to the browser here put the
      // browser's day beside a UTC clock, which can name a different day than
      // the time next to it. One clock for both halves or the line contradicts
      // itself.
      // Retry order matters, and an earlier version got it wrong in both
      // directions: retrying with `tz` reused the very value that threw (when
      // the item has no zone, `tz` IS `timezone`), and retrying with nothing
      // put the browser's day beside a school-clock time.
      //
      // So: try the item's zone, then the school's ONLY if it is a different
      // value, then no zone at all — which cannot throw. Whatever zone wins is
      // the one the window is formatted in too, so the two halves never sit on
      // different clocks; if none wins, the window is dropped rather than
      // rendered against a day it does not match.
      // ⚠ A zone must satisfy BOTH formatters to be used. The `Intl` attempt
      // alone is not validation for the window: `formatBookingWindow` formats
      // with moment, which does not throw on a name it lacks data for — it
      // logs and returns UTC — so an ICU-known/moment-unknown zone would put
      // this day on the listing's clock beside a window an hour or more away.
      // ⚠ One zone for BOTH halves, and never none. `resolveRowTimezone` picks
      // the clock this row's own picker used and falls back to the browser's;
      // dropping the window when nothing validated cost every row on this
      // screen its time for a whole school at once — `school.timezone` is
      // free-form, so "GMT+1" is storable. The catch covers the narrow case
      // where Intl rejects a name moment accepts: both halves fall to the
      // browser together rather than splitting, so the fallback asks for the
      // browser's zone DIRECTLY rather than spelling it as an online row.
      let usedZone = resolveRowTimezone(item, timezone);
      let day: string;
      try {
        day = dayFmt(usedZone);
      } catch {
        usedZone = resolveDisplayTimezone();
        day = dayFmt(undefined);
      }
      // Same zone as the day, or no window at all — never a day on one clock
      // beside a time on another.
      const bookingWindow = formatBookingWindow({ item, timezone: usedZone });
      return bookingWindow ? `${day}, ${bookingWindow}` : day;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

export function BasketCheckoutOverlay({
  businessId,
  apiUrl = DEFAULT_API_URL,
  primaryColor = "#03c2b9",
  gtagConversionId,
  gtagConversionLabel,
  paymentReturn,
}: BasketCheckoutOverlayProps) {
  const queryClient = getQueryClient();

  // The school's checkout settings — the same cached answer a pass or
  // product overlay that opened this checkout already fetched
  // (lib/school-checkout-config). Loading, failed or ready: nothing payable
  // is offered until they're here (checkoutGate.ts).
  const school = useSchoolCheckoutConfig<SchoolConfig>(businessId, apiUrl);
  const schoolConfig = school.status === "ready" ? school.config : null;

  const [buyerDetails, setBuyerDetails] = useState<BuyerDetails>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    marketingOptIn: true,
  });

  // Customer session (auth-store). This overlay can be the only Baluu
  // element on the page (opened directly from `<baluu-basket>`, with no
  // `<baluu-login>` present to hydrate the session first), so it validates
  // any stored token itself — hydrateSession is idempotent per page.
  const auth = useAuthSnapshot();
  const isLoggedIn = auth.status === "authenticated";
  // Bundles, membership credits and points are spent from the SIGNED-IN
  // account only, never from whatever email the buyer form holds.
  const accountEmail = isLoggedIn ? auth.user?.email ?? null : null;
  const [authOpen, setAuthOpen] = useState(false);
  const openLogin = () => setAuthOpen(true);

  useEffect(() => {
    void hydrateSession(apiUrl);
  }, [apiUrl]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The last press's refusal of the basket's contents, if that's what it was
  // (see `errorSlotFor`: it only speaks while `error` is its message).
  const [contentsRefusal, setContentsRefusal] = useState<ContentsRefusal | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  // Which completion screen to show: a request sent for approval
  // ("requested") or a placed order ("paid", including free orders).
  const [completionVariant, setCompletionVariant] = useState<
    "paid" | "requested" | "deposit_requested"
  >("paid");
  // A request whose deposit is paid now (session `requestDeposit`): what
  // the completion screen quotes once the card payment succeeds.
  const [depositRequestSummary, setDepositRequestSummary] = useState<{
    depositPence: number;
    remainingPence: number;
  } | null>(null);
  // The booking fee the created session quoted. Read only while that session
  // exists (`isPaymentReady`); before it, the Total carries the first-paint
  // estimate from the disclosed rate.
  const [quotedBookingFeePence, setQuotedBookingFeePence] = useState(0);
  // Baluu's service fee and the authoritative fee-inclusive charge the same
  // session quoted. At the pay step the summary shows these, so the total on
  // screen is what the card is charged (ruling R32).
  const [quotedServiceFeePence, setQuotedServiceFeePence] = useState(0);
  const [quotedChargePence, setQuotedChargePence] = useState<number | null>(null);
  const [serverRequestToBook, setServerRequestToBook] =
    useState<ServerRequestToBookAnswer | null>(null);

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  // Where Stripe sends the buyer when the payment method has to leave the
  // page; null where it can't come back (a non-http page).
  const [stripeReturnUrl, setStripeReturnUrl] = useState<string | null>(null);
  // "confirming" while a Stripe redirect's outcome is being settled.
  const [returnPhase, setReturnPhase] = useState<"none" | "confirming">(
    paymentReturn ? "confirming" : "none",
  );
  const returnPhaseRef = useRef(returnPhase);
  // Accepted but settling later (a bank debit): the confirmation says so.
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [stripePaymentData, setStripePaymentData] =
    useState<StripePaymentData | null>(null);
  const [successData, setSuccessData] = useState<SuccessData | null>(null);

  // Every code the buyer has applied, with what the validate call returned
  // for it: one unless the school allows stacking (shared rules,
  // lib/checkout/discount-codes).
  const [appliedDiscounts, setAppliedDiscounts] = useState<AppliedDiscount[]>([]);

  // Paying with an owned bundle or with membership credits instead of the
  // card, picked in FinalStep's payment-method radio. Either one skips
  // Stripe: the order is confirmed against it (confirmFreeBasketOrder).
  const [selectedPaymentBundleId, setSelectedPaymentBundleId] = useState<string | null>(null);
  const [selectedMembershipId, setSelectedMembershipId] = useState<string | null>(null);
  const [selectedPaymentOption, setSelectedPaymentOption] =
    useState<CheckoutPaymentOption>("card");
  // Loyalty points opt-in, per order. The server spends them only for a
  // verified sign-in matching the buyer, and recomputes every figure. Kept
  // as the sign-in it was ticked under (see `redeemPoints` below).
  const [redeemPointsFor, setRedeemPointsFor] = useState<string | null>(null);

  const [termsDialogData, setTermsDialogData] = useState<{
    isOpen: boolean;
    termsSettings?: TermsSettings;
    customTerms?: CustomTerms;
    timezone?: string;
    ticketPolicies?: any[];
  }>({ isOpen: false });

  /**
   * How many validates are in flight, not whether one is — BOTH effects below
   * gate on it now, and a boolean let the faster one report "done" while the
   * other was still asking. It matters because the blocker banner draws
   * `&& !isValidating`: with only the mount validate counted, the whole
   * debounce-plus-round-trip of the email validate displayed the email-LESS
   * verdicts, which are precisely the false positives that effect exists to
   * clear — and offered a button that discards the buyer's form.
   */
  const [validatesInFlight, setValidatesInFlight] = useState(0);
  const isValidating = validatesInFlight > 0;
  // For the reservation heartbeat, which must not race either of them.
  const validatesInFlightRef = useRef(0);

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsTouched, setTermsTouched] = useState(false);
  const [deliveryDetails, setDeliveryDetails] = useState<DeliveryAddressValues>(
    EMPTY_DELIVERY_ADDRESS,
  );
  const mandatoryTickBox =
    !!schoolConfig?.termsSettings?.termsAndConditions?.mandatoryTickBox;

  const isNewsletterEnabled = !!schoolConfig?.isNewsletterSignupEnabled;
  const newsletterLabel =
    (schoolConfig?.customNewsletterSignupText &&
      String(schoolConfig.customNewsletterSignupText).trim()) ||
    `Sign up to the ${schoolConfig?.name ?? "this business"} newsletter to occasionally receive news, promotions and event information. You can unsubscribe at any time.`;

  const basketOrderIdRef = useRef<string | null>(null);
  const validationDoneRef = useRef(false);
  const isCheckoutSubmittingRef = useRef(false);
  // What the last email-validate was asked about. See the effect below: the
  // ids and the email are tracked apart so a changed email re-asks about an
  // unchanged basket. Mirrors useBasketSync's pair in the widget.
  const lastValidatedRef = useRef<string | null>(null);
  const lastValidatedEmailRef = useRef<string>("");
  /**
   * Whether the last email validate came back with nothing usable — a non-2xx,
   * an abort on the timeout, or a dropped connection. Read by the Pay guard,
   * which re-asks rather than refusing on verdicts it could not refresh.
   */
  const lastValidateFailedRef = useRef(false);
  /**
   * Whether the Pay guard has already spent its one re-ask on the current run
   * of failures. Re-armed only by a validate that actually lands.
   *
   * Without it the re-ask never terminates: the failure flag above is
   * rewritten by every completed validate, so against an endpoint that keeps
   * failing each Pay click re-entered the retry, and the buyer never reached
   * the branch that would tell them what is wrong.
   */
  const hasRetriedRef = useRef(false);
  /**
   * Whether the error currently on screen came from the server refusing the
   * basket on availability. Such an error stops being true the moment the
   * offending item is removed, but it is raised through the generic Pay catch
   * — so without this it outlived its cause, sitting red under a banner that
   * had already cleared, until the buyer clicked Pay again.
   */
  const serverRefusedOnAvailabilityRef = useRef(false);
  /** Bumped to force the debounced validate to run again; see the Pay guard. */
  const [revalidateNonce, setRevalidateNonce] = useState(0);
  /**
   * Which validate is the current one. The mount validate and the debounced
   * email validate are independent effects and can be in flight together, so
   * without this the slower one wins on arrival order rather than on being the
   * better-informed answer.
   */
  const validateSeqRef = useRef(0);

  const items = useSyncExternalStore(
    (cb) => basketStore.subscribe(cb),
    () => basketStore.getItems(businessId),
  );

  // Login gate: gift cards are exempt (matching the widget basket checkout
  // and the CheckoutOrchestrator's own gift-card exemption), so a
  // gift-card-only basket is never gated; any bookable item (booking/
  // product/bundle) re-arms it. Uses the shared shouldGateCheckout
  // predicate so the gate rules can't drift from the orchestrator's.
  const gate = basketCheckoutGate({
    config: school.status,
    requireLogin: !!schoolConfig?.requireLoginToBook,
    isLoggedIn,
    hasGateableItem: items.some((item) => item.type !== "giftcard"),
  });
  const showLoginGate = gate === "sign-in";

  // Prefer the server-validated `currentPrice` (written by the pre-checkout
  // validate effect below when it differs from the stamped price) so the
  // displayed total matches the amount `CreateBasketCheckoutSession` charges.
  // `??` not `||` so a real £0 price (free / membership-covered / 100%-off)
  // wins over the stamped figure.
  // Product surcharges (postage per unit, gift wrap per line) are outside the
  // per-unit price and CreateBasketCheckoutSession adds them to the
  // PaymentIntent, so they're in the total too (the widget's rule).
  const total = items.reduce(
    (sum, i) =>
      sum + (i.currentPrice ?? i.price) * i.quantity + productSurcharge(i),
    0,
  );
  const itemCount = countBasketItems(items);

  // Derived from the items rather than latched from a validate response, which
  // is what the old `hasUnavailableItems` state did: that flag only moved when
  // a validate finished, so removing the offending item left it stuck true and
  // a cross-tab change never reached it at all. The rule itself lives with
  // `countBlockingItems` and its tests: everything blocks EXCEPT
  // `VALIDATION_TRANSIENT` and the two partial codes, which matches the drawer
  // and the widget. (An earlier draft of this branch did block `partial` here;
  // that was reversed — see the reasoning in `countBlockingItems`.)
  const blockingItemCount = useMemo(() => countBlockingItems(items), [items]);

  // See `identityBlockers`: when the only thing standing between the
  // buyer and checkout is WHO they said they are, the drawer is the wrong
  // place to send them - it has no email field and its anonymous revalidate
  // would wipe the verdict, showing a clean basket that blocks them again the
  // moment they return. The fix is here, in the field they already have.
  const identityBlock = useMemo(() => identityBlockers(items), [items]);
  /**
   * Whether a `<baluu-basket>` is actually on this page.
   *
   * Declared up here because the copy below depends on it. The surfaces that
   * open this overlay (CheckoutOverlay, the purchase overlays,
   * CalendarVisibility) put one on the page only when they offer the basket
   * (their `basket` attribute: Website v2), and every other embed keeps the
   * basket off. So neither the "Review basket" button nor any advice to
   * remove an item can be offered unconditionally.
   *
   * Re-checked whenever a blocker appears rather than read once during render:
   * the element is injected lazily on some hosts, and a render-time query
   * could answer "no drawer" and never be asked again.
   */
  const [canReviewBasket, setCanReviewBasket] = useState(false);
  useEffect(() => {
    if (typeof document === "undefined") return;
    setCanReviewBasket(basketElementFor(businessId) !== null);
  }, [businessId, blockingItemCount, isValidating, contentsRefusal]);

  // Whether to WITHHOLD the drawer. Only when EVERY blocker is
  // email-remediable: the drawer would erase those verdicts and loop the
  // buyer back. As soon as a used-up trial is among them — alone or mixed in —
  // its only remedy is removing the ticket, and the drawer is where that
  // happens, so the button must stay.
  const blockedOnIdentity =
    identityBlock !== null && identityBlock.emailRemediable;

  // Each code has its own remedy, and `defaultUnavailabilityMessage` already
  // draws the distinction — "available to members only" is not "limited to
  // selected customers", and telling a member to check their address sends
  // them looking for a typo that isn't there.
  const identityBlockCopy = useMemo(() => {
    if (!identityBlock) return null;
    const plural = identityBlock.count > 1;
    // The trial allowance is the one kind whose remedy is not a different
    // email: the address is right, the allowance is what's short. The drawer
    // (where the ticket is removed) is NOT withheld for it — see
    // `blockedOnIdentity`. The headline comes from the item's own verdict,
    // because the same code covers "already used", "only one of your two is
    // left" and "at most one per basket" — a hardcoded "already used" would
    // contradict the item card for a first-time buyer with two in the basket.
    if (identityBlock.kind === "trial_limit") {
      const verdicts = items
        .map((i) => i.unavailability)
        .filter((u): u is NonNullable<typeof u> => u?.code === "TRIAL_LIMIT_REACHED");
      const headlines = Array.from(new Set(verdicts.map((u) => defaultUnavailabilityMessage(u))));
      const headline =
        headlines.length === 1 ? headlines[0] : "Your basket has more trial tickets than you can book";
      // Unlike the members-only/tag-gated remedies, this one lives in the
      // DRAWER, not on this surface. Three of the four hosts that open this
      // overlay have no <baluu-basket>, and "Review basket" is withheld
      // there — so telling those buyers to remove an item would name an
      // action they have no control to take.
      if (!canReviewBasket) {
        return `${headline}. We can't take payment for this basket as it stands — please contact the school.`;
      }
      return plural
        ? `${headline}. Remove the extra trial tickets from your basket, or book regular tickets instead.`
        : `${headline}. Remove the trial ticket from your basket, or book a regular ticket instead.`;
    }
    // Mixed WITH a trial over the allowance: no single email fixes this
    // basket, so name both moves — the generic "restricted" copy below would
    // send the buyer hunting for an email that clears the trial, and there
    // is none.
    if (!identityBlock.emailRemediable) {
      // "mixed" here covers members-only OR tag-gated alongside the trial, and
      // those two have different email advice — the distinction the rest of
      // this copy was split apart to preserve.
      const membersOnly = items.some(
        (i) => i.unavailability?.code === "TICKET_MEMBERS_ONLY",
      );
      const emailAdvice = membersOnly
        ? "Use the email address on your membership"
        : "Check the email address above";
      return canReviewBasket
        ? `Some items need a different email address, and a trial ticket is over your allowance for its class. ${emailAdvice}, and remove the trial ticket from your basket.`
        : `Some items need a different email address, and a trial ticket is over your allowance for its class. ${emailAdvice} — we can't take payment for the trial ticket, so please contact the school.`;
    }
    const what =
      identityBlock.kind === "members_only"
        ? plural
          ? "They're members-only tickets."
          : "It's a members-only ticket."
        : identityBlock.kind === "tag_gated"
          ? plural
            ? "They're limited to selected customers."
            : "It's limited to selected customers."
          : "They're restricted to selected customers.";
    const remedy =
      identityBlock.kind === "members_only"
        ? "Use the email address on your membership"
        : "Check the email address above";
    // No "or remove it from your basket". This branch is reached only past
    // the two early returns above — not the trial kind, and
    // `emailRemediable` true — which is precisely `blockedOnIdentity`, the
    // condition under which the "Review basket" button is withheld (see the
    // `canReviewBasket && !blockedOnIdentity` render): sending an identity
    // blocker to a drawer that revalidates anonymously erases the verdict
    // and loops the buyer straight back. Offering the remedy while
    // suppressing the only affordance for it was the mirror image of the bug
    // this copy was split to fix. The email field it names is on this
    // surface and always works. (`identityBlockCopy` as a whole renders for
    // every kind; the trial kind keeps the button, and says so above.)
    return `${what} ${remedy}.`;
  }, [identityBlock, items, canReviewBasket]);

  // Retract the Pay-time refusal once the items it named are resolved. The
  // debounced email validate exists precisely to clear false positives from
  // the email-less mount validate, so a buyer who clicks Pay a moment too
  // early would otherwise watch the banner disappear while the red error
  // underneath it stayed, until they clicked Pay again. Scoped to this one
  // message so it cannot swallow a Stripe or form error.
  useEffect(() => {
    if (isValidating) return;
    // Decided BEFORE setError, and the ref cleared outside the updater. React
    // re-runs a queued updater from the base state when a render is
    // interrupted and restarted, and an updater that mutates is not safe to
    // replay: the second pass would see the ref already false, fall through,
    // and leave the server's message on screen for good.
    const retractServerRefusal =
      serverRefusedOnAvailabilityRef.current && blockingItemCount === 0;
    if (retractServerRefusal) serverRefusedOnAvailabilityRef.current = false;

    setError((current) => {
      if (!current) return current;
      if (retractServerRefusal) return null;
      if (!BLOCKED_ITEMS_ERRORS.has(current)) return current;
      // Nothing blocks any more: every refusal in the set is spent.
      if (blockingItemCount === 0) return null;
      // Something still blocks, but "still checking" has stopped being true.
      // Leaving it there put a message saying we did not know yet directly
      // under a banner naming how many items need attention. The banner is
      // the better explanation, so drop the placeholder rather than swap in
      // a refusal the buyer did not just ask for.
      return current === BLOCKED_ITEMS_ERROR_CHECKING ? null : current;
    });
  }, [blockingItemCount, isValidating]);

  const currency =
    schoolConfig?.preferredCurrency || items[0]?.currency || "GBP";
  const timezone = schoolConfig?.timezone || "Europe/London";
  // Always the card form here, deliberately NOT the school's
  // `schoolConfig.paymentMode` (its stripeConnect.stripeHostedPage, which the
  // widget's full-page checkout follows). CreateBasketCheckoutSession never
  // opens Stripe's hosted page for a webcomponent checkout (this overlay
  // reports sourceType "webcomponent"), and stripeHostedPage is switched on
  // for every school at Stripe connect, so following it would label nearly
  // every Pay button "Continue to Payment Page" over a card form. The flow
  // still follows the SERVER's answer: a hosted session (`outcome.kind ===
  // "hosted"`) redirects, and <baluu-basket> handles its return.
  const paymentMode = "embedded" as const;

  const isPaymentReady = !!clientSecret;

  // Prefill buyer details from the signed-in customer's saved profile, once,
  // when login flips true and the buyer hasn't already started the form
  // (`buyerDetails.email` still empty) — never overwrites an in-progress
  // edit. Best-effort: a flaky /me/profile lookup must never block checkout,
  // so errors are swallowed and the buyer just fills the form as normal.
  useEffect(() => {
    if (!isLoggedIn || !auth.token || buyerDetails.email) return;
    const baseUrl = apiOrigin(apiUrl);
    fetch(`${baseUrl}/widget/me/profile`, {
      headers: { Authorization: `Bearer ${auth.token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        const d = body?.data;
        if (!d) return;
        setBuyerDetails((prev) => ({
          ...prev,
          firstName: prev.firstName || d.firstName || "",
          lastName: prev.lastName || d.lastName || "",
          email: prev.email || d.email || "",
          phone: prev.phone || d.phone || "",
        }));
      })
      .catch(() => {});
  }, [isLoggedIn, auth.token]);

  // The school's checkout questions ("Your information") and its built-in
  // buyer-phone row, from the same endpoint and on the same rules as the
  // widget's basket checkout.
  const {
    customerFields,
    customerFieldAnswers,
    customerFieldsStatus,
    updateCustomerFieldAnswer,
    buyerPhoneConfig,
    linkedFromAttendee,
  } = useBasketCustomerFields({
    apiUrl,
    businessId,
    items,
    isLoggedIn,
    authToken: auth.token,
  });
  const currencySymbol = CurrencyISO__CurrencySymbol(currency);

  // The signed-in customer's bundles and membership for this basket, and
  // their loyalty standing — the widget's checkout options, on its rules.
  const { eligibleBundles, membershipResolution } = useBasketPaymentOptions({
    apiUrl,
    businessId,
    items,
    accountEmail,
  });
  const loyaltySummary = useBasketLoyalty({
    apiUrl,
    businessId,
    authToken: isLoggedIn ? auth.token : null,
  });
  // The opt-in belongs to the sign-in it was given under: signed out the
  // block is hidden, so a tick must not ride along unseen, nor carry over to
  // another account.
  const redeemPoints =
    !!loyaltySummary && !!auth.token && redeemPointsFor === auth.token;
  const setRedeemPoints = useCallback(
    (next: boolean) => setRedeemPointsFor(next ? auth.token ?? null : null),
    [auth.token],
  );
  const allowStacking = !!schoolConfig?.basket?.allowDiscountStacking;

  // A pick that no longer pays for this basket (signed out, the basket
  // changed, the bundle ran out) stops counting rather than being sent: the
  // server would refuse it, or spend it on a basket it wasn't chosen for.
  const liveBundleId =
    selectedPaymentBundleId &&
    eligibleBundles.some((b) => b.id === selectedPaymentBundleId)
      ? selectedPaymentBundleId
      : null;
  const liveMembershipId =
    selectedMembershipId &&
    membershipResolution?.subscriptionId === selectedMembershipId
      ? selectedMembershipId
      : null;

  // Pre-checkout validation: check item availability before the buyer fills
  // in their details. Email-less, so the server counts this buyer's own holds
  // among "others" — the debounced effect below re-asks once it knows who
  // they are.
  useEffect(() => {
    if (validationDoneRef.current || items.length === 0) return;
    validationDoneRef.current = true;

    (async () => {
      const seq = ++validateSeqRef.current;
      setValidatesInFlight((n) => n + 1);
      try {
        const results = await validateBasketItems({
          apiUrl,
          schoolId: businessId,
          items,
          // Whatever any surface on this page has already learned. This
          // overlay is rebuilt from scratch every time checkout opens
          // (`openBasketCheckout` replaces the element, and `buyerDetails`
          // starts empty), so without it a buyer who reached Pay, created
          // holds and came back was measured against his OWN holds and told
          // the basket was sold out — the exact failure the store's email
          // channel was added to stop, left in place on the one surface that
          // was not wired to it.
          buyerEmail: basketStore.getKnownBuyerEmail(),
          // The flag is about the SURFACE, not this call: the buyer can
          // correct the address here, so an identity refusal is actionable.
          canActOnIdentityVerdicts: true,
        });
        // This one is email-LESS, so it is the weaker answer of the two. On a
        // slow connection it can land after the buyer has typed their email
        // and the debounced validate has already cleared its false positives
        // - re-stamping them would be permanent, because that effect's dedup
        // key is already claimed and will not fire again for the same basket.
        if (seq !== validateSeqRef.current) return;
        if (results) basketStore.applyValidationResults(results);
      } catch {
        // Non-blocking: checkout-session creation re-validates server-side,
        // so an unreachable endpoint must not strand the buyer here.
      } finally {
        // Unconditionally, unlike the `apply` above: a superseded call is
        // still a call that has finished. The counter is what keeps that
        // honest now — an earlier boolean version, guarded by the same
        // sequence check as the apply, was never lowered when the email
        // validate overtook this one, and the spinner stayed for the life of
        // the overlay.
        setValidatesInFlight((n) => n - 1);
      }
    })();
  }, [items, apiUrl, businessId]);

  // Re-validate once the buyer has typed a valid-looking email so the server
  // can exclude THIS buyer's own holds from the "others" sum, and so
  // buyer-dependent pricing (MEMBER_PRICING, tag gates) resolves. Debounced so
  // we don't ask per keystroke.
  useEffect(() => {
    if (items.length === 0) return;
    const email = (buyerDetails.email || "").trim();
    if (!email || !validateEmail(email).isValid) return;

    // Dedup key, and it is load-bearing rather than an optimisation. Applying
    // a response rewrites every matched item, which mints new object
    // identities, which changes `items` — so this effect re-ran on its own
    // result and the 500ms debounce became a ~2Hz poll for as long as the
    // overlay stayed open. The ids are what actually decide the answer, and
    // they do not change when the objects do. `force` is not needed here: no
    // caller on this surface polls.
    const itemsKey = items.map((i) => i.id).join(",");
    if (
      lastValidatedRef.current === itemsKey &&
      lastValidatedEmailRef.current === email
    ) {
      return;
    }

    const timer = setTimeout(async () => {
      // Claimed as the timer fires, not when it is scheduled, so a rescheduled
      // debounce still runs. The email is tracked separately from the ids: a
      // buyer correcting their address must be re-asked even though the basket
      // is unchanged, or an eligible buyer's tag-gated item stays flagged.
      lastValidatedRef.current = itemsKey;
      lastValidatedEmailRef.current = email;
      // Publish it before asking. The drawer has no email field and its
      // Checkout is the only way back here, so an anonymous validate there
      // would measure the buyer against their OWN holds and hard-block a
      // basket that is genuinely theirs.
      basketStore.setKnownBuyerEmail(email);
      const seq = ++validateSeqRef.current;
      let landed = false;
      setValidatesInFlight((n) => n + 1);
      try {
        const results = await validateBasketItems({
          apiUrl,
          schoolId: businessId,
          items,
          buyerEmail: email,
          // The one surface that can: the buyer can correct the address here.
          canActOnIdentityVerdicts: true,
        });
        if (seq !== validateSeqRef.current) return;
        // `null` is a refusal (non-2xx), not an answer - so it must NOT count
        // as landed, or the dedup key below is held for a 500 and the buyer
        // stays on the email-less mount validate's false positives with
        // nothing able to re-ask. Which is the very thing that comment claims
        // to prevent.
        landed = results !== null;
        if (results) basketStore.applyValidationResults(results);
      } catch {
        // Non-blocking, as above.
      } finally {
        setValidatesInFlight((n) => n - 1);
        // Release the dedup key unless this call actually produced an answer.
        //
        // What this does NOT do is re-ask: the effect's deps are the items,
        // the email and the urls, none of which change when a request fails,
        // so releasing the key re-triggers nothing. It earns its place in a
        // narrower case — the validity check above returns before any claim,
        // so a buyer who backspaces their address and retypes THE SAME one
        // would otherwise match the key claimed by the failed attempt and be
        // skipped. Any changed email or basket misses the key anyway.
        //
        // The buyer who changes nothing after a failure is handled at the Pay
        // guard, which re-asks rather than refusing on a verdict it could not
        // refresh.
        //
        // Only THIS call's claim, though. A superseded one also reaches here
        // with `landed` false, and the item ids do not change when the buyer
        // merely corrects their email — so without the sequence check it would
        // release the key its successor had just taken, costing one redundant
        // round trip per correction.
        if (seq === validateSeqRef.current) {
          // Only the current call may record the outcome: a superseded one
          // also arrives with `landed` false, and its successor is the answer
          // that counts.
          lastValidateFailedRef.current = !landed;
          // A good answer earns the buyer another re-ask if things break later.
          if (landed) hasRetriedRef.current = false;
          if (!landed && lastValidatedRef.current === itemsKey) {
            lastValidatedRef.current = null;
            lastValidatedEmailRef.current = "";
          }
        }
      }
    }, VALIDATE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [items, buyerDetails.email, apiUrl, businessId, revalidateNonce]);

  /**
   * Apply a validate-shaped answer that is NOT one of the two effects above —
   * the reservation heartbeat's, or the reserve route's — only if neither of
   * those started a newer validate meanwhile. They never claim a sequence
   * number, so they cannot supersede the better-informed answer or disturb
   * the dedup and failure bookkeeping that hangs off it.
   */
  const applyIfStillCurrent = useCallback(() => {
    const seq = validateSeqRef.current;
    return (results: BasketValidationResult[]) => {
      if (seq === validateSeqRef.current) {
        basketStore.applyValidationResults(results);
      }
    };
  }, []);

  // What the reservation heartbeat reads when it fires: it outlives renders.
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
    validatesInFlightRef.current = validatesInFlight;
    returnPhaseRef.current = returnPhase;
  });

  // Seat holds while checkout is open (v1's semantics; see useBasketHolds).
  const { releaseItem } = useBasketHolds({
    apiUrl,
    schoolId: businessId,
    items,
    buyerEmail: buyerDetails.email || "",
    // Not while a Stripe redirect's outcome settles: that basket is being
    // paid, and holding its seats again could leave fresh holds behind
    // after fulfilment consumed the order's own.
    reservationsEnabled:
      !!schoolConfig?.basket?.reservationsEnabled && returnPhase === "none",
    userId: isLoggedIn ? auth.user?.id : undefined,
    isSuccess,
    isPaused: () =>
      isCheckoutSubmittingRef.current || returnPhaseRef.current !== "none",
    heartbeat: (email) => {
      // Skipped while either effect above is asking: that answer is about to
      // land and is at least as fresh.
      if (validatesInFlightRef.current > 0) return;
      const apply = applyIfStillCurrent();
      validateBasketItems({
        apiUrl,
        schoolId: businessId,
        items: itemsRef.current,
        buyerEmail: email,
        canActOnIdentityVerdicts: true,
      })
        .then((results) => {
          if (results) apply(results);
        })
        .catch(() => {
          // Non-blocking; the next beat asks again.
        });
    },
    beginApply: applyIfStillCurrent,
  });

  const handleClose = useCallback(() => {
    // An abandoned card step leaves nothing to return to (a real redirect
    // return navigates away, it never closes the overlay).
    if (basketOrderIdRef.current) forgetBasketPaymentReturn(basketOrderIdRef.current);
    const overlays = document.querySelectorAll("baluu-basket-checkout-overlay");
    overlays.forEach((overlay) => overlay.parentNode?.removeChild(overlay));
  }, []);

  /**
   * Whether anything would be LOST by taking the banner's advice. Reviewing
   * the basket closes this overlay, and `openBasketCheckout` builds a fresh
   * element when the buyer returns - so the name, email, phone, address and
   * ticked terms all go with it. The route still has to exist (the drawer is
   * the only place a flagged item can be fixed), but recommending it without
   * saying so costs a buyer their whole form.
   */
  const hasStartedForm = Boolean(
    buyerDetails.firstName ||
    buyerDetails.lastName ||
    buyerDetails.email ||
    buyerDetails.phone ||
    // The doc above promises "name, email, phone, address and ticked terms",
    // and these two are lost with the rest. A buyer on a shipped-product
    // basket who filled the address and accepted the terms but has not typed
    // a name yet was told nothing before losing both.
    acceptedTerms ||
    Object.values(deliveryDetails).some(
      (v) => typeof v === "string" && v.trim(),
    ),
  );

  /**
   * Hand the buyer back to the drawer, which is where a flagged item can
   * actually be resolved — BasketItemStatus renders the reason and its fix
   * there, and this overlay has no per-item UI at all. The drawer revalidates
   * on open, so they land on a fresh verdict.
   *
   * Offered ONLY when `canReviewBasket` says a `<baluu-basket>` is on the
   * page. Without that check, closing the overlay on a page without one
   * dispatches an event nothing is listening for: no drawer, no badge, and the
   * buyer is left on the host's marketing page with a blocked basket and
   * nothing on screen. Same rule as the auto-fix buttons in BasketItemStatus —
   * don't render an action that cannot act.
   */
  const handleReviewBasket = useCallback(() => {
    if (!basketElementFor(businessId)) return;
    handleClose();
    requestBasketDrawer(businessId);
  }, [handleClose, businessId]);

  // The one "Review basket" button, for the needs-attention banner and a
  // refusal of the basket's contents; each caller decides when it's offered.
  const reviewBasketButton = (
    <button
      type="button"
      onClick={handleReviewBasket}
      className="shrink-0 h-7 px-2.5 rounded-md border border-destructive/30 text-xs font-medium text-destructive hover:bg-destructive/10"
    >
      Review basket
    </button>
  );

  // A spent refusal goes, message and all (unless another error has already
  // replaced it): nothing should keep reading an error the buyer can no
  // longer see. Adjusted while rendering when the basket changes (React's
  // "storing information from previous renders"), not in an effect, so the
  // stale message never paints.
  if (contentsRefusal && contentsRefusalSpent(contentsRefusal, items)) {
    setContentsRefusal(null);
    if (error === contentsRefusal.message) setError(null);
  }

  // What the error under the CTA says, and whether it's a refusal of the
  // basket's contents still in force (then it says how to fix it).
  const errorSlot = errorSlotFor(error, contentsRefusal, items);

  // The needs-attention banner (availability blockers). When it's up it
  // owns the way into the basket, so a refusal's error doesn't offer a
  // second "Review basket" — and both withhold it for an email-remediable
  // blocker (see `blockedOnIdentity`: the drawer would erase the verdict).
  const showNeedsAttention = blockingItemCount > 0 && !isValidating && !isPaymentReady;
  const refusalOffersBasket =
    errorSlot.basketFix && canReviewBasket && !blockedOnIdentity && !showNeedsAttention;

  // Close when basket is empty (and not in success/payment state)
  useEffect(() => {
    if (
      items.length === 0 &&
      !isSuccess &&
      !isPaymentReady &&
      returnPhase === "none" &&
      // A failed redirect return must stay up long enough to say so.
      error !== PAYMENT_RETURN_FAILED
    ) {
      handleClose();
    }
  }, [items.length, isSuccess, isPaymentReady, returnPhase, error, handleClose]);

  // Map basket items to FinalStep's OrderItem format
  const orderItems: OrderItem[] = useMemo(() => {
    return items.flatMap((item) => {
      const result: OrderItem[] = [];
      // Free its seat holds first, as the widget's checkout does.
      const handleRemove = () => {
        releaseItem(item.id);
        basketStore.removeItem(item.id);
      };
      const dateDesc = getItemDateDescription(item, timezone);

      if (item.ticketSelections && item.ticketSelections.length > 0) {
        // When the server-validated `currentPrice` differs from the cached
        // stamped price (capacity-based / early-bird / member / cart-rule
        // pricing), reconcile the per-line ticket totals so the rows sum to
        // the grand `total` shown at the bottom (computed with the same
        // `(currentPrice ?? price) * quantity` basis above).
        //
        // The server's `currentPrice` INCLUDES add-ons at full price
        // (ValidateBasketItems: `currentPrice = ticketTotal + addOnTotal`,
        // add-ons never discounted). We render add-ons as their own face-value
        // lines below, so the discount must land on the ticket rows only:
        // subtract the add-on total to get the amount the ticket rows must sum
        // to. Clamp at 0 so an add-on-heavy membership / 100%-off item can't
        // produce negative ticket rows. (This intentionally diverges from the
        // widget, whose ratio omits this subtraction and double-counts add-ons
        // when both a reprice and add-ons are present.)
        const addOnTotal = (item.addOns ?? []).reduce(
          (sum, a) => sum + a.price * a.quantity,
          0,
        );
        const cachedTicketTotal = item.ticketSelections.reduce(
          (sum, ts) => sum + (ts.lineTotal ?? ts.price * ts.quantity),
          0,
        );
        // `currentPrice` is only set when validation ran AND repriced; absent →
        // keep today's stamped line totals. `??` not `||`: a legitimately-£0
        // currentPrice (free / 100%-off) must still reconcile.
        const targetTicketTotal =
          item.currentPrice != null
            ? Math.max(
                0,
                (item.currentPrice ?? item.price) * (item.quantity || 1) -
                  addOnTotal,
              )
            : cachedTicketTotal;
        // Distribute `targetTicketTotal` across the ticket rows proportionally
        // to their cached weight, giving the rounding remainder to the last
        // row so the rows sum EXACTLY to the target (rounding each row
        // independently would drift a penny off the grand total). When there's
        // no cached weight to scale by (all stamped line totals 0 but the
        // server now charges), split evenly so the rows still sum to the target
        // instead of all showing 0.
        // Deposit items are NOT reconciled by weight: `currentPrice` is the
        // deposit-due-now, not a reprice of the contract, so scaling the
        // full-price lines to it smears the deposit across rows (£11.31 /
        // £6.69 / £9.00 for three £9 deposits). Use the server's per-line
        // split instead and show each line as "deposit / full".
        const depositByTicket = item.isDeposit
          ? new Map(
              (item.ticketDeposits ?? [])
                .filter((d) => typeof d.depositPence === "number")
                .map((d) => [d.ticketTypeId, d]),
            )
          : null;
        // Mixed deposit item (deposit + full-price lines): the full-price
        // lines must reconcile against what's left of the target AFTER the
        // deposit lines take their fixed shares — weighted against each
        // other only, with the LAST full-price row absorbing the remainder
        // (regardless of row order). For non-deposit items these collapse to
        // the original whole-item figures.
        const isDepLine = (ts: (typeof item.ticketSelections)[number]) =>
          !!depositByTicket?.get(ts.ticketTypeId);
        const depositLinesTotal = item.ticketSelections.reduce(
          (sum, ts) =>
            sum + (depositByTicket?.get(ts.ticketTypeId)?.depositPence ?? 0),
          0,
        );
        const nonDepTarget = Math.max(0, targetTicketTotal - depositLinesTotal);
        const nonDepCachedTotal = item.ticketSelections.reduce(
          (sum, ts) =>
            isDepLine(ts)
              ? sum
              : sum + (ts.lineTotal ?? ts.price * ts.quantity),
          0,
        );
        const lastNonDepIdx = item.ticketSelections.reduce(
          (last, ts, i) => (isDepLine(ts) ? last : i),
          -1,
        );
        const nonDepCount = item.ticketSelections.filter(
          (ts) => !isDepLine(ts),
        ).length;
        let allocatedTicketTotal = 0;
        item.ticketSelections.forEach((ts, idx) => {
          const baseLineTotal = ts.lineTotal ?? ts.price * ts.quantity;
          const dep = depositByTicket?.get(ts.ticketTypeId);
          let lineTotal: number;
          if (dep) {
            lineTotal = dep.depositPence!;
          } else if (idx === lastNonDepIdx) {
            lineTotal = nonDepTarget - allocatedTicketTotal;
          } else if (nonDepCachedTotal > 0) {
            lineTotal = Math.round(
              (baseLineTotal / nonDepCachedTotal) * nonDepTarget,
            );
          } else {
            lineTotal = Math.round(nonDepTarget / Math.max(1, nonDepCount));
          }
          if (!dep) allocatedTicketTotal += lineTotal;
          result.push({
            name: `${item.courseName || item.productName || "Item"} — ${ts.ticketTypeName}`,
            description: idx === 0 ? dateDesc : undefined,
            price: ts.price,
            lineTotal,
            fullLineTotal: dep ? dep.lineTotalPence : undefined,
            count: ts.quantity,
            type: "ticket",
            imageUrl: idx === 0 ? item.imageUrl : undefined,
            onRemove: idx === 0 ? handleRemove : undefined,
            bookingPolicy: ts.bookingPolicy ?? null,
          });
        });
      } else {
        result.push({
          name:
            item.courseName ||
            item.productName ||
            (item.type === "giftcard" ? "Gift Card" : "Item"),
          description: dateDesc,
          price: item.price,
          count: item.quantity || 1,
          type: "ticket",
          imageUrl: item.imageUrl,
          onRemove: handleRemove,
        });
      }

      if (item.addOns) {
        item.addOns.forEach((addOn) => {
          result.push({
            name: addOn.addOnName,
            price: addOn.price,
            count: addOn.quantity,
            type: "addon",
          });
        });
      }

      // A product's postage and gift wrap as their own rows, so the rows add
      // up to the total (shared with the widget's basket checkout).
      result.push(...productSurchargeLines(item));

      return result;
    });
  }, [items, timezone, releaseItem]);

  // Request-to-book: every item is a booking on a ticket the partner confirms
  // before taking any money (`requiresApproval`, stamped by the checkout
  // orchestrator when the item was added). The CTA becomes "Send request" and
  // no card form opens. The add-time flag can go stale while the item sits in
  // the basket, so once the server has classified THIS basket its
  // `isRequestToBook` wins in both directions.
  const requestItemsKey = useMemo(() => requestToBookItemsKey(items), [items]);
  // A request whose deposit is paid when it is sent goes through the card
  // form like a deposit checkout; only its copy differs.
  const isDepositAtRequest = useMemo(() => isDepositAtRequestBasket(items), [items]);
  const isRequestToBook = useMemo(
    () =>
      resolveIsRequestToBook(
        classifyRequestToBookBasket(items) === "all" && !isDepositAtRequest,
        serverRequestToBook,
        requestItemsKey,
      ),
    [items, serverRequestToBook, requestItemsKey, isDepositAtRequest],
  );
  // Discount handling. The add / combine rules, the validate body and the
  // codes sent are the widget checkout's (lib/checkout/discount-codes).
  const handleApplyDiscount = useCallback(
    async (code: string) => {
      const trimmed = code.trim();
      if (!trimmed) return;
      const addError = discountCodeAddError({
        code: trimmed,
        applied: appliedDiscounts,
        allowStacking,
      });
      if (addError) throw new Error(addError);

      const baseUrl = apiOrigin(apiUrl);
      const response = await fetch(`${baseUrl}/widget/discounts/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          discountValidateBody({
            code: trimmed,
            email: buyerDetails.email || "",
            schoolId: items[0]?.schoolId || businessId,
            items,
          }),
        ),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(
          errData.message || errData.error || "Invalid discount code",
        );
      }

      const data = await response.json();
      const combineError = validatedDiscountError({
        discount: data.discount,
        applied: appliedDiscounts,
        isRequestToBook,
      });
      if (combineError) throw new Error(combineError);
      // Kept exactly as validated: gift card codes are stored lowercase and
      // the checkout-time lookup is an exact match.
      setAppliedDiscounts((prev) => [...prev, { code: trimmed, data: data.discount }]);
    },
    [items, buyerDetails.email, apiUrl, businessId, appliedDiscounts, allowStacking, isRequestToBook],
  );

  // No code: the single-code field's remove (there is only one).
  const handleRemoveDiscount = useCallback((code?: string) => {
    setAppliedDiscounts((prev) => (code ? prev.filter((d) => d.code !== code) : []));
  }, []);

  // The widget's split, shared: the contract is discounted, a deposit item
  // only gives up its now-share today, the rest lands on its balance.
  const discountShares = useMemo(
    () => computeDiscountShares(appliedDiscounts, items, total),
    [appliedDiscounts, items, total],
  );
  const discountAmount = discountShares.now;

  // Deposit-at-checkout summary: what's still owed after today's deposit
  // (net of the discount's balance-leg share).
  const depositRemainingTotal = useMemo(() => {
    const contractRemainder = items.reduce(
      (sum, i) => sum + (i.isDeposit ? (i.remainingBalance ?? 0) : 0),
      0,
    );
    return Math.max(0, contractRemainder - discountShares.later);
  }, [items, discountShares.later]);

  const discountPercentage = singleCodeDiscountPercentage(
    appliedDiscounts,
    allowStacking,
  );
  // The single-code field shows the code; with stacking the codes field does.
  const singleDiscountCode = !allowStacking ? appliedDiscounts[0]?.code : undefined;

  const finalTotal = Math.max(0, total - discountAmount);
  // A deposit order with an outstanding balance is never "free" even if the
  // now-share zeroes the deposit charged today — the balance is still owed, and
  // the server returns a real clientSecret. Guards the CTA / PaymentElement path.
  // The balance is read net of the code's later-share: a code that covers the
  // whole contract (e.g. 100% off, add-ons included — DEV-327) leaves nothing
  // owed, and the server takes its free path.
  const isFreeBooking = finalTotal === 0 && depositRemainingTotal <= 0;

  // The line under the pay button of a deposit-at-request basket.
  const depositAtRequestNote =
    !isRequestToBook && (isDepositAtRequest || depositRequestSummary)
      ? describeDepositAtRequest(finalTotal, depositRemainingTotal, (p) => formatPrice(p, currency))
      : null;

  // What the order is redeemed against, if anything (never on a request).
  const { paymentBundleId, paymentMembershipId } = resolveRedemption({
    isRequestToBook,
    selectedPaymentBundleId: liveBundleId,
    selectedMembershipId: liveMembershipId,
  });
  const isRedemption = !!(paymentBundleId || paymentMembershipId);
  // The chosen method can't be submitted yet ("bundle" with none picked).
  const paymentPickError = isRequestToBook
    ? null
    : paymentOptionError({
        selectedPaymentOption,
        selectedPaymentBundleId: liveBundleId,
        selectedMembershipId: liveMembershipId,
      });
  // "Free (Bundle)" / "Free (Membership)" in place of the amount.
  const redemptionLabel = redemptionTotalLabel({
    isRequestToBook,
    selectedPaymentBundleId: liveBundleId,
    selectedMembershipId: liveMembershipId,
  });
  // Points can't be spent on a deposit basket (the server answers 400
  // LOYALTY_POINTS_DEPOSIT_UNSUPPORTED), so the block says so instead.
  const hasDepositItem = items.some((i) => i.isDeposit);

  // School-set booking fee, in its own row and inside the Total from first
  // paint (Website v2 checks out through this overlay too). Until the session
  // exists it is an estimate from the checkout-config's disclosed rate on the
  // server's base — pay-now + a gift card applied (R24: a payment, not a
  // price change) + the deposit balance owed later; once the session exists
  // its quote replaces it, even a quote of 0. A bundle / membership
  // redemption is confirmed without a session and carries no fee; nothing is
  // paid as a partner link here.
  const bookingFeeDisclosure = readBookingFeeDisclosure(schoolConfig);
  const isGiftCardApplied = appliedDiscounts.some((d) => isGiftCardDiscount(d.data));
  const estimatedBookingFeePence = estimateBookingFeePence({
    disclosure: bookingFeeDisclosure,
    payNowMinor: finalTotal,
    // The card's whole contribution: the share spent today AND the share
    // baked into a deposit's balance, as the server adds back.
    giftCardAppliedMinor: isGiftCardApplied ? discountShares.now + discountShares.later : 0,
    laterBalanceMinor: depositRemainingTotal,
    // `redeemedFromBundleId` deliberately not consulted, as on the server
    // (ruling R32): a real redemption is a free order and carries no fee.
    hasBookingItem: items.some((item) => item.type === "booking"),
    isRedemption,
    isRequestToBook,
  });
  const bookingFeePence = resolveDisplayedBookingFee(
    isPaymentReady ? quotedBookingFeePence : null,
    estimatedBookingFeePence,
  );

  // Pay-step figures (ruling R32), mirroring the widget/Youmo: once the
  // session exists its quoted fees get their own rows and the total shown is
  // the server's fee-inclusive charge. Before that, the subtotal plus the
  // booking-fee estimate (Baluu's service fee keeps its own first-paint note).
  const payStepServiceFeePence = isPaymentReady ? quotedServiceFeePence : 0;
  const displayTotal = isPaymentReady
    ? quotedChargePence ?? finalTotal + bookingFeePence + payStepServiceFeePence
    : finalTotal + bookingFeePence;

  const bookingFeeDetail = bookingFeeDetailText(
    bookingFeeDisclosure,
    currency,
    (minor, cur) => formatPrice(minor, cur),
  );

  // Create checkout session
  const needsDeliveryAddress = useMemo(
    () => items.some(itemNeedsDelivery),
    [items],
  );

  const handleCreateCheckoutSession = useCallback(async () => {
    if (isCheckoutSubmittingRef.current) return;
    // Belt and braces for the gate the render applies: no session for a
    // checkout whose school settings aren't here, or a buyer who must sign in.
    if (gate !== "open") return;

    // Whatever the last attempt left on screen is about to be replaced by this
    // one's outcome, so the flag describing it goes too. Only the `!response.ok`
    // branch below re-raises it, which means a network failure, a Stripe
    // failure or a failed free-order confirm can no longer INHERIT an earlier
    // availability refusal's retraction and have its own message silently
    // erased. Deliberately after the re-entrancy guard: a double-click that
    // returns as a no-op must not clear a flag belonging to the attempt still
    // in flight.
    serverRefusedOnAvailabilityRef.current = false;

    // Only when there is something to conceal. While a validate is in flight
    // the verdicts on screen are the known-stale email-less ones, which is why
    // the banner hides itself — so refusing on them would be refusing on data
    // the UI will not show. But an unconditional guard here refused a CLEAN
    // basket too: a buyer whose details autofill and who clicks Pay inside the
    // debounce got a red error with nothing behind it, and the retraction
    // below then cleared that error the moment the validate landed, so the
    // click read as having done nothing at all.
    //
    // A clean basket falls through instead. CreateBasketCheckoutSession
    // re-validates with the buyer's email and refuses anything unavailable,
    // so the server, not this guard, is what actually protects the sale.
    if (isValidating && blockingItemCount > 0) {
      setError(BLOCKED_ITEMS_ERROR_CHECKING);
      return;
    }

    if (blockingItemCount > 0) {
      // Refuse on a verdict we could not refresh and the buyer is stuck. The
      // blocker may be a false positive from the email-LESS mount validate,
      // which counts their own reservation holds against them, and the email
      // validate that would have cleared it failed with nothing to re-trigger
      // it. So ask once more before saying no — but only when that attempt
      // actually failed, so a genuinely sold-out basket still explains itself
      // on the first click rather than the second.
      const retryEmail = (buyerDetails.email || "").trim();
      if (
        lastValidateFailedRef.current &&
        !hasRetriedRef.current &&
        retryEmail &&
        validateEmail(retryEmail).isValid
      ) {
        hasRetriedRef.current = true;
        lastValidatedRef.current = null;
        lastValidatedEmailRef.current = "";
        setRevalidateNonce((n) => n + 1);
        setError(BLOCKED_ITEMS_ERROR_CHECKING);
        return;
      }

      setError(
        identityBlock
          ? identityBlock.kind === "members_only"
            ? BLOCKED_ITEMS_ERROR_IDENTITY_MEMBERS
            : identityBlock.kind === "trial_limit"
              ? canReviewBasket
                ? BLOCKED_ITEMS_ERROR_IDENTITY_TRIAL
                : BLOCKED_ITEMS_ERROR_IDENTITY_TRIAL_NO_DRAWER
              : !identityBlock.emailRemediable
                ? BLOCKED_ITEMS_ERROR_IDENTITY_MIXED_TRIAL
                : BLOCKED_ITEMS_ERROR_IDENTITY_RESTRICTED
          : canReviewBasket
            ? BLOCKED_ITEMS_ERROR
            : BLOCKED_ITEMS_ERROR_NO_DRAWER,
      );
      return;
    }

    if (!buyerDetails.firstName || !buyerDetails.lastName) {
      setError("Please fill in your name to continue");
      return;
    }

    // validateEmail owns all email messaging: empty → "Email is required",
    // malformed (e.g. no TLD like "user@gmail") → "Please enter a valid
    // email address" — mirrors the widget basket checkout so bad addresses
    // are caught inline instead of via a generic backend 400.
    const emailResult = validateEmail(buyerDetails.email || "");
    if (!emailResult.isValid) {
      setError(emailResult.error || "Please enter a valid email address");
      return;
    }

    // Phone is gated by the school's built-in phone row, not unconditionally
    // — shouldEnforceBuyerPhone is the single source of truth shared with
    // the widget basket checkout so the rule can't silently diverge between
    // the two copies. shouldEnforceBuyerPhone itself treats "dial code only,
    // no digits" (e.g. "+44" from opening the country dropdown and picking a
    // country without typing anything — PhoneCountryInput's country-select
    // path does NOT normalize that back to "", only its digit path does) as
    // not-supplied, so an untouched optional field still passes and a
    // filled-but-malformed one still fails even when optional.
    if (shouldEnforceBuyerPhone(buyerPhoneConfig, buyerDetails.phone || "")) {
      const phoneResult = validatePhone(buyerDetails.phone || "");
      if (!phoneResult.isValid) {
        setError(phoneResult.error || "Please enter a valid phone number");
        return;
      }
    }

    if (mandatoryTickBox && !acceptedTerms) {
      setTermsTouched(true);
      setError("Please accept the terms and conditions to continue");
      return;
    }

    // The school's questions: not before they have arrived (the buyer would
    // skip a required one they haven't seen yet), and never on a fetch that
    // failed — the server then skips its required check too.
    if (customerFieldsStatus === "idle") {
      setError(CUSTOMER_FIELDS_LOADING_ERROR);
      return;
    }
    const customerFieldError = validateCustomerFieldAnswers(
      customerFields,
      customerFieldAnswers,
      customerFieldsStatus,
    );
    if (customerFieldError) {
      setError(customerFieldError);
      return;
    }

    if (needsDeliveryAddress && !isDeliveryAddressComplete(deliveryDetails)) {
      setError("Please fill in the delivery address for your order");
      return;
    }

    if (paymentPickError) {
      setError(paymentPickError);
      return;
    }

    isCheckoutSubmittingRef.current = true;
    setIsLoading(true);
    setError(null);

    /**
     * An order placed with nothing charged — free, or paid with a bundle /
     * membership — is complete: report it (its gross value, 0 charged), empty
     * the basket and show the confirmation.
     */
    const completeUnchargedOrder = (orderId: string) => {
      dispatchCompletedEvent(orderId, 0, items.length);
      basketStore.clearBasket(businessId);

      // Free bookings still report their real (gross) value per our
      // conversion-tracking guide; net charged is 0. Mirror the basket event
      // name (dataLayer only) for forward compatibility.
      trackConversion(
        "baluu_booking_completed",
        {
          businessId,
          purchaseId: orderId,
          grossTotal: total,
          netTotal: 0,
          total: 0,
          discountAmount,
          currency,
          email: buyerDetails.email,
        },
        gtagConversionId && gtagConversionLabel
          ? {
              conversionId: gtagConversionId,
              conversionLabel: gtagConversionLabel,
            }
          : undefined,
        ["baluu_basket_checkout_completed"],
      );

      // Documented `baluu:booking-completed` DOM event (Custom JavaScript
      // conversion approach) — basket purchases included.
      document.dispatchEvent(
        new CustomEvent("baluu:booking-completed", {
          bubbles: true,
          composed: true,
          detail: {
            businessId,
            purchaseId: orderId,
            grossTotal: total,
            netTotal: 0,
            total: 0,
            discountAmount,
            currency,
            email: buyerDetails.email,
          },
        }),
      );

      setIsSuccess(true);
      setSuccessData({
        purchaseId: orderId,
        total: 0,
        currency,
        email: buyerDetails.email,
      });
    };

    try {
      const sourceInfo = getSourceInfo();

      const requestItems = items.map((item) => ({
        id: item.id,
        type: item.type,
        schoolId: item.schoolId,
        courseId: item.courseId,
        sessionId: item.sessionId,
        productId: item.productId,
        productSlug: item.productSlug,
        bundleTemplateId: item.bundleTemplateId,
        bundleName: item.bundleName,
        bundleDescription: item.bundleDescription,
        bundleTotalSessions: item.bundleTotalSessions,
        bundleExpiresIn: item.bundleExpiresIn,
        bundleExpirationPeriodType: item.bundleExpirationPeriodType,
        ticketSelections: item.ticketSelections,
        addOns: item.addOns,
        quantity: item.quantity,
        price: item.price,
        currency: item.currency,
        courseName: item.courseName,
        productName: item.productName,
        sessionDate: item.sessionDate,
        sessionEndDate: item.sessionEndDate,
        imageUrl: item.imageUrl,
        appointmentData: item.appointmentData,
        termData: item.termData,
        isOnline: item.isOnline,
        bookingType: item.bookingType,
        checkoutGroupId: item.checkoutGroupId,
        // Product-specific fields
        postageId: item.postageId,
        postagePrice: item.postagePrice,
        // One order-level address, attached per shipping product item — the
        // server model (productPurchase.deliveryAddress) is per-item.
        // deliveryDetails already uses the server's key casing (postCode).
        deliveryAddress: itemNeedsDelivery(item)
          ? {
              ...deliveryDetails,
              firstName: buyerDetails.firstName,
              lastName: buyerDetails.lastName,
              contactNumber: buyerDetails.phone || "",
            }
          : undefined,
        // Product gifting and the product checkout's guests, as the widget
        // sends them (buildBasketItemsPayload): processBasketItem maps
        // giftingDetails onto the ProductPurchase.
        isGift: item.isGift,
        giftingDetails: item.isGift ? item.giftingDetails : undefined,
        checkoutGuestsInformation: item.checkoutGuestsInformation,
        // Gift card fields
        recipientEmail: item.recipientEmail,
        recipientName: item.recipientName,
        senderName:
          item.senderName ||
          `${buyerDetails.firstName} ${buyerDetails.lastName}`,
        giftCardMessage: item.message,
        giftCardAmount: item.giftcardAmount,
        selectedImage: item.imageUrl,
        userInputs: item.userInputs,
        participants: item.participants,
      }));

      const baseUrl = apiOrigin(apiUrl);
      const buyer = {
        email: buyerDetails.email.toLowerCase(),
        firstName: buyerDetails.firstName,
        lastName: buyerDetails.lastName,
        phone: buyerDetails.phone,
        marketingOptIn: buyerDetails.marketingOptIn,
      };
      // Order-level, beside `buyer`, as the widget sends it. Absent (not [])
      // when the fields didn't load.
      const answers = customerFieldAnswersForPayload(
        customerFields,
        customerFieldAnswers,
        customerFieldsStatus,
      );
      const checkoutSourceInfo = { ...sourceInfo, sourceType: "webcomponent" };
      // The signed-in customer's bearer: the server reads the verified email
      // from it for loyalty (the tier perk, and points only when it matches
      // the buyer). Absent when signed out — such a buyer gets no loyalty.
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(isLoggedIn && auth.token
          ? { Authorization: `Bearer ${auth.token}` }
          : {}),
      };

      // Paid with a bundle or membership credits: no Stripe session. The
      // server creates the order from these items and confirms it against
      // the bundle / membership, exactly as the widget checkout does.
      if (isRedemption) {
        const redeemRes = await fetch(
          `${baseUrl}/widget/app/confirmFreeBasketOrder`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(
              redemptionConfirmBody({
                items: requestItems,
                buyer,
                customerFieldAnswers: answers,
                discountCodes: discountCodesForPayload(appliedDiscounts),
                sourceInfo: checkoutSourceInfo,
                paymentBundleId,
                paymentMembershipId,
              }),
            ),
          },
        );
        const redeemed = await redeemRes.json().catch(() => ({}));
        if (!redeemRes.ok || !redeemed.basketOrderId) {
          serverRefusedOnAvailabilityRef.current = Array.isArray(
            redeemed.unavailableItems,
          );
          throw new BasketApiError(
            redeemed.message ||
              redeemed.error ||
              (paymentBundleId
                ? "Failed to confirm your booking with your bundle"
                : "Failed to confirm your booking with your membership"),
            { code: redeemed.code, status: redeemRes.status },
          );
        }
        completeUnchargedOrder(redeemed.basketOrderId);
        return;
      }

      const response = await fetch(
        `${baseUrl}/widget/app/createBasketCheckoutSession`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            items: requestItems,
            buyer,
            customerFieldAnswers: answers,
            discountCodes: discountCodesForPayload(appliedDiscounts),
            redeemPoints: redeemPointsForPayload({
              redeemPoints,
              isRequestToBook,
              hasDepositItem,
            }),
            source: "widget",
            sourceInfo: checkoutSourceInfo,
            returnUrl: window.location.href,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Remember whether the server refused on AVAILABILITY specifically —
        // it says so by returning the per-item array. Only that kind of error
        // becomes untrue when the basket is fixed, so only that kind may be
        // retracted below; a Stripe or configuration failure from the same
        // catch must stay on screen.
        serverRefusedOnAvailabilityRef.current = Array.isArray(
          errorData.unavailableItems,
        );
        throw new BasketApiError(
          errorData.message ||
            errorData.error ||
            "Failed to create checkout session",
          { code: errorData.code, status: response.status },
        );
      }

      const result = await response.json();
      basketOrderIdRef.current = result.basketOrderId;
      setQuotedBookingFeePence(Number(result.bookingFeePence) || 0);
      setQuotedServiceFeePence(Number(result.serviceFeePence) || 0);
      setQuotedChargePence(
        typeof result.amount === "number" && Number.isFinite(result.amount)
          ? result.amount
          : null,
      );
      setServerRequestToBook({
        itemsKey: requestItemsKey,
        isRequestToBook: isUnpaidRequestResponse(result),
      });
      setDepositRequestSummary(
        isDepositRequestResponse(result)
          ? { depositPence: finalTotal, remainingPence: depositRemainingTotal }
          : null,
      );
      const outcome = resolveOverlaySessionOutcome(result);

      // Request-to-book order: nothing is charged now. Confirm the order the
      // session just created, exactly like a free one; the partner reviews
      // it and emails a payment link. Not a purchase, so none of the
      // completed/conversion events below — host pages get
      // `baluu_booking_requested` instead.
      if (
        outcome.kind === "confirm" &&
        outcome.completionVariant === "requested"
      ) {
        await confirmBasketOrder(
          baseUrl,
          result.basketOrderId,
          "Failed to send your request",
        );
        basketStore.clearBasket(businessId);
        dispatchBookingRequested(result.basketOrderId);
        setCompletionVariant("requested");
        setIsSuccess(true);
        return;
      }

      // Free order
      if (outcome.kind === "confirm") {
        await confirmBasketOrder(
          baseUrl,
          result.basketOrderId,
          "Failed to confirm free order",
        );
        completeUnchargedOrder(result.basketOrderId);
        return;
      }

      // Hosted Stripe page
      if (outcome.kind === "hosted") {
        // The buyer is about to leave the page for Stripe's hosted checkout and
        // return on a fresh page load (where the in-memory basket is gone).
        // Stash the order value so the return handler in Basket.tsx can fire a
        // conversion that carries value/currency/email.
        try {
          window.sessionStorage.setItem(
            `baluu_basket_conv_${result.basketOrderId}`,
            JSON.stringify({
              grossTotal: total,
              netTotal: finalTotal,
              total: finalTotal,
              discountAmount,
              currency,
              email: buyerDetails.email,
            }),
          );
        } catch {
          // sessionStorage may be unavailable (private mode); the conversion
          // will still fire on return, just without value/currency.
        }
        window.location.href = outcome.url;
        return;
      }

      // Embedded Stripe - use portal
      const stripeKey =
        result.stripePublicKey || schoolConfig?.stripePublicKey || "";

      if (stripeKey) {
        const paymentMethodType = result.paymentMethodType || "destination";
        // Kept for the trip to Stripe and back, should the method need a
        // redirect (3-D Secure off-page, bank redirects): the page reloads on
        // return and the bundle finishes the order from this.
        rememberBasketPaymentReturn(result.basketOrderId, {
          businessId,
          apiUrl,
          primaryColor,
          gtagConversionId,
          gtagConversionLabel,
          stripePublicKey: stripeKey,
          stripeAccount:
            paymentMethodType === "direct" && result.schoolAccountID
              ? result.schoolAccountID
              : undefined,
          grossTotal: total,
          netTotal: finalTotal,
          total: finalTotal,
          discountAmount,
          currency,
          email: buyerDetails.email,
          itemCount: items.length,
          depositRequest: isDepositRequestResponse(result)
            ? { depositPence: finalTotal, remainingPence: depositRemainingTotal }
            : null,
        });
        setStripeReturnUrl(
          basketPaymentReturnUrl(window.location.href, {
            businessId,
            basketOrderId: result.basketOrderId,
          }),
        );
        setClientSecret(result.clientSecret);
        setStripePaymentData({
          clientSecret: result.clientSecret,
          stripePublicKey: stripeKey,
          paymentMethodType,
          schoolAccountID: result.schoolAccountID,
          purchaseId: result.basketOrderId,
          buyerEmail: buyerDetails.email,
          total: result.amount,
          currency,
        });
      }
    } catch (err: any) {
      const message = err.message || "Something went wrong. Please try again.";
      setError(message);
      // This press's items: the basket the request was built from, even if
      // the buyer changed it while the request was in flight.
      setContentsRefusal(contentsRefusalFor(message, err?.code, items));
    } finally {
      setIsLoading(false);
      isCheckoutSubmittingRef.current = false;
    }
  }, [
    items,
    buyerDetails,
    buyerPhoneConfig,
    customerFields,
    customerFieldAnswers,
    customerFieldsStatus,
    appliedDiscounts,
    paymentPickError,
    isRedemption,
    paymentBundleId,
    paymentMembershipId,
    redeemPoints,
    isRequestToBook,
    hasDepositItem,
    isLoggedIn,
    auth.token,
    apiUrl,
    businessId,
    currency,
    schoolConfig,
    gate,
    blockingItemCount,
    identityBlock,
    canReviewBasket,
    isValidating,
    mandatoryTickBox,
    acceptedTerms,
    needsDeliveryAddress,
    deliveryDetails,
    total,
    finalTotal,
    discountAmount,
    gtagConversionId,
    gtagConversionLabel,
    requestItemsKey,
    primaryColor,
    depositRemainingTotal,
  ]);

  /**
   * A paid basket order, finished: recorded with the API (which also covers a
   * missed webhook), the basket emptied, the conversion counted, and the
   * confirmation shown. Reached in place from the card form, and after a
   * Stripe redirect from the return (`paymentReturn`), where the figures come
   * from what was kept for the trip rather than from this page's state.
   */
  const finishPaidOrder = useCallback(
    async ({
      basketOrderId,
      paymentIntentId,
      figures,
      depositRequest,
      processing = false,
      countConversion = true,
      successData: base,
    }: {
      basketOrderId: string | null;
      paymentIntentId?: string;
      figures: {
        grossTotal?: number;
        netTotal?: number;
        discountAmount?: number;
        currency: string;
        email?: string;
        itemCount: number;
      };
      depositRequest: { depositPence: number; remainingPence: number } | null;
      /** Accepted but settling later (bank debit). */
      processing?: boolean;
      /** False when nothing confirmed the payment is real (see the return
       *  effect): the buyer still sees their confirmation, but no purchase is
       *  reported for an address anyone could type. */
      countConversion?: boolean;
      successData?: SuccessData;
    }): Promise<boolean> => {
      setIsSubmitting(true);
      let processedOk = false;
      // processBasketPayment answers 200 for any existing order, paid or
      // not; only its `paymentStatus` says the server saw the money.
      let serverConfirmedPaid = false;
      try {
        if (basketOrderId) {
          const baseUrl = apiOrigin(apiUrl);
          const res = await fetch(
            `${baseUrl}/widget/app/processBasketPayment`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ basketOrderId, paymentIntentId }),
            },
          );
          processedOk = res.ok;
          if (res.ok) {
            const body = await res.json().catch(() => null);
            serverConfirmedPaid = body?.paymentStatus === "PAID";
          }
        }
      } catch {
        // Payment already succeeded; processing happens via webhooks
      }

      if (processedOk || basketOrderId) {
        basketStore.clearBasket(businessId);
      }
      if (basketOrderId) forgetBasketPaymentReturn(basketOrderId);

      if (countConversion || serverConfirmedPaid) {
        dispatchCompletedEvent(
          basketOrderId || "",
          figures.netTotal ?? 0,
          figures.itemCount,
        );

        const conversion = {
          businessId,
          purchaseId: basketOrderId || undefined,
          grossTotal: figures.grossTotal,
          netTotal: figures.netTotal,
          total: figures.netTotal,
          discountAmount: figures.discountAmount,
          currency: figures.currency,
          email: figures.email,
        };
        // Fire the documented purchase event (`baluu_booking_completed`) so
        // the GTM trigger schools set up per our conversion-tracking guide
        // matches, plus the zero-code Google Ads conversion when the gtag
        // attributes are present. `grossTotal` is the pre-discount sum;
        // `netTotal` the amount actually charged. Also mirror the
        // basket-specific event name (dataLayer only) for forward
        // compatibility.
        trackConversion(
          "baluu_booking_completed",
          conversion,
          gtagConversionId && gtagConversionLabel
            ? {
                conversionId: gtagConversionId,
                conversionLabel: gtagConversionLabel,
              }
            : undefined,
          ["baluu_basket_checkout_completed"],
        );

        // Documented `baluu:booking-completed` DOM event (Custom JavaScript
        // conversion approach) — basket purchases included.
        document.dispatchEvent(
          new CustomEvent("baluu:booking-completed", {
            bubbles: true,
            composed: true,
            detail: conversion,
          }),
        );
      }

      if (depositRequest) {
        setDepositRequestSummary(depositRequest);
        setCompletionVariant("deposit_requested");
      }
      // Neither Stripe nor the server vouched for the payment (an unverified
      // return the server hasn't seen paid yet): say it is still processing
      // rather than promise a confirmation that may never come.
      setPaymentProcessing(processing || (!countConversion && !serverConfirmedPaid));
      setIsSuccess(true);
      setSuccessData({
        ...base,
        purchaseId: basketOrderId || base?.purchaseId,
        total: figures.netTotal,
        currency: figures.currency,
        email: figures.email,
      });
      return processedOk;
    },
    [apiUrl, businessId, gtagConversionId, gtagConversionLabel],
  );

  // Handle Stripe payment success (in place, no redirect)
  const handleStripeSuccess = useCallback(
    async (data: SuccessData) => {
      await finishPaidOrder({
        basketOrderId: basketOrderIdRef.current,
        paymentIntentId: data.paymentIntentId,
        figures: {
          grossTotal: total,
          netTotal: finalTotal,
          discountAmount,
          currency,
          email: buyerDetails.email,
          itemCount: items.length,
        },
        depositRequest: depositRequestSummary,
        processing: data.paymentProcessing,
        successData: data,
      });
    },
    [
      finishPaidOrder,
      total,
      finalTotal,
      discountAmount,
      items.length,
      currency,
      buyerDetails.email,
      depositRequestSummary,
    ],
  );

  // Back from a Stripe redirect (3-D Secure off-page, a bank redirect):
  // finish the order, or put the basket back with a notice. Once per
  // overlay; the return's keys are already off the address.
  const paymentReturnStartedRef = useRef(false);
  useEffect(() => {
    if (!paymentReturn || paymentReturnStartedRef.current) return;
    paymentReturnStartedRef.current = true;
    void (async () => {
      const { outcome, verified } = await verifyPaymentReturn(paymentReturn);
      if (outcome === "failed") {
        setError(PAYMENT_RETURN_FAILED);
        setReturnPhase("none");
        return;
      }
      markReturnHandled(paymentReturn.paymentIntentId);
      const ctx = paymentReturn.context;
      await finishPaidOrder({
        basketOrderId: paymentReturn.basketOrderId,
        paymentIntentId: paymentReturn.paymentIntentId,
        figures: {
          grossTotal: ctx?.grossTotal,
          netTotal: ctx?.netTotal,
          discountAmount: ctx?.discountAmount,
          currency: ctx?.currency || currency,
          email: ctx?.email,
          itemCount: ctx?.itemCount ?? items.length,
        },
        depositRequest: ctx?.depositRequest ?? null,
        processing: outcome === "processing",
        countConversion: verified,
      });
      setReturnPhase("none");
    })();
    // Once, on mount: the return is the overlay's opening state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStripeClose = useCallback(() => {
    // The card step is gone, so is the reason to keep its return context: a
    // later attempt creates a new order and keeps its own.
    if (basketOrderIdRef.current) forgetBasketPaymentReturn(basketOrderIdRef.current);
    setStripePaymentData(null);
    setClientSecret(null);
    setStripeReturnUrl(null);
  }, []);

  const handleOpenTermsDialog = useCallback(
    (data: {
      termsSettings?: TermsSettings;
      customTerms?: CustomTerms;
      timezone?: string;
      ticketPolicies?: any[];
    }) => {
      setTermsDialogData({ isOpen: true, ...data });
    },
    [],
  );

  const handlePolicyClick = useCallback((ticketName: string, policy: any) => {
    setTermsDialogData({
      isOpen: true,
      ticketPolicies: [{ ticketName, policy }],
    });
  }, []);

  const handleCloseTermsDialog = useCallback(() => {
    setTermsDialogData((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleBuyerChange = useCallback(
    (field: keyof PersonalDetailsValues, value: string | boolean) => {
      if (field === "mandatoryTickBox") return;
      setBuyerDetails((d) => ({ ...d, [field]: value as never }));
    },
    [],
  );

  // Buyer details content (passed to FinalStep as beforeOrderSummary)
  const buyerDetailsContent = useMemo(
    () => (
      <CollapsibleSection
        title="Your Details"
        icon={<User className="w-4 h-4 text-muted-foreground" />}
        defaultOpen={!isPaymentReady}
      >
        <div className="p-4 space-y-4">
          <PersonalDetailsForm
            values={{
              firstName: buyerDetails.firstName,
              lastName: buyerDetails.lastName,
              email: buyerDetails.email,
              phone: buyerDetails.phone,
              marketingOptIn: buyerDetails.marketingOptIn,
            }}
            onChange={handleBuyerChange}
            showPhone={buyerPhoneConfig.askAtCheckout}
            phoneRequired={buyerPhoneConfig.isRequired}
            showMarketingOptIn={isNewsletterEnabled}
            marketingOptInLabel={newsletterLabel}
            businessCountry={schoolConfig?.businessCountry}
          />
          {customerFields.length > 0 && (
            <div className="space-y-3 pt-2">
              <p className="text-sm font-medium text-muted-foreground">
                Your information
              </p>
              {/* Answered already on the buyer's own attendee card: shown
                  back rather than asked again (it's on its way with the
                  order). */}
              {Object.keys(linkedFromAttendee).length > 0 && (
                <div className="space-y-1 rounded-lg border bg-muted/40 p-3 text-sm">
                  <p className="text-muted-foreground">
                    Collected on the attendee step:
                  </p>
                  {customerFields
                    .filter((field) => field.slug in linkedFromAttendee)
                    .map((field) => (
                      <p key={field.slug}>
                        <span className="font-medium">{field.label}:</span>{" "}
                        {formatLinkedCustomerFieldValue(linkedFromAttendee[field.slug])}
                      </p>
                    ))}
                </div>
              )}
              {/* portalContainer={null}: the date picker opens inline, inside
                  this shadow root, where its styles are. */}
              <CheckoutCustomerFields
                fields={customerFields.filter(
                  (field) => !(field.slug in linkedFromAttendee),
                )}
                answers={customerFieldAnswers}
                onAnswerChange={updateCustomerFieldAnswer}
                currencySymbol={currencySymbol}
                defaultCountryCode={schoolConfig?.businessCountry}
                portalContainer={null}
              />
            </div>
          )}
          {mandatoryTickBox && (
            <MandatoryAcceptanceCheckbox
              checked={acceptedTerms}
              onCheckedChange={(checked) => {
                setAcceptedTerms(checked);
                setTermsTouched(true);
              }}
              onOpenTerms={() =>
                handleOpenTermsDialog({
                  termsSettings: schoolConfig?.termsSettings,
                  customTerms: schoolConfig?.customTerms,
                  timezone: schoolConfig?.timezone,
                })
              }
              touched={termsTouched}
            />
          )}
        </div>
      </CollapsibleSection>
    ),
    [
      buyerDetails,
      buyerPhoneConfig,
      customerFields,
      customerFieldAnswers,
      updateCustomerFieldAnswer,
      linkedFromAttendee,
      currencySymbol,
      isPaymentReady,
      handleBuyerChange,
      mandatoryTickBox,
      acceptedTerms,
      termsTouched,
      schoolConfig,
      handleOpenTermsDialog,
      isNewsletterEnabled,
      newsletterLabel,
    ],
  );

  // Delivery address for shipping product items (rendered under the buyer
  // details, same collapsible idiom). Locked once the payment session exists:
  // the address was snapshotted into the BasketOrder at session creation, so
  // a later edit here would be silently discarded — freeze the inputs
  // instead of collecting corrections that never reach the server.
  const deliveryAddressContent = useMemo(() => {
    if (!needsDeliveryAddress) return null;
    return (
      <CollapsibleSection
        title="Delivery Address"
        icon={<Package className="w-4 h-4 text-muted-foreground" />}
        defaultOpen={!isPaymentReady}
      >
        <div className="p-4">
          <DeliveryAddressForm
            values={deliveryDetails}
            disabled={isPaymentReady}
            onChange={(field, value) =>
              setDeliveryDetails((d) => ({ ...d, [field]: value }))
            }
          />
        </div>
      </CollapsibleSection>
    );
  }, [needsDeliveryAddress, deliveryDetails, isPaymentReady]);

  // Several codes when the school allows stacking (FinalStep's own field
  // then stays off); one otherwise, in FinalStep's field.
  const stackingDiscountContent = allowStacking ? (
    <div className="bg-card rounded-xl border border-border shadow-sm px-5 py-3.5">
      <DiscountCodesSection
        appliedDiscounts={appliedDiscounts}
        onApply={handleApplyDiscount}
        onRemove={handleRemoveDiscount}
      />
    </div>
  ) : null;

  // The signed-in customer's points (and tier perk). Nothing at all without
  // a standing at this school, and never on a request (no money collected).
  const loyaltyContent =
    loyaltySummary && !isRequestToBook ? (
      <div className="bg-card rounded-xl border border-border shadow-sm px-5 py-3.5">
        <LoyaltyCheckoutSection
          summary={loyaltySummary}
          currency={currencySymbol}
          redeemPoints={redeemPoints}
          onToggleRedeem={setRedeemPoints}
          blockedByDeposit={hasDepositItem}
        />
      </div>
    ) : null;

  const beforeOrderSummaryContent = (
    <>
      {buyerDetailsContent}
      {deliveryAddressContent}
      {/* Locked with the session: the codes and the points opt-in are
          snapshotted into the order when it is created. */}
      {!isPaymentReady && stackingDiscountContent}
      {!isPaymentReady && loyaltyContent}
    </>
  );

  // Order summary sidebar (desktop)
  const orderSummarySidebar = useMemo(() => {
    const ticketItems = orderItems.filter(
      (i) => i.type === "ticket" && i.count > 0,
    );
    const addonItems = orderItems.filter(
      (i) => i.type === "addon" && i.count > 0,
    );

    return (
      <div className="hidden lg:block">
        <div className="sticky top-6">
          <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
            <div className="px-5 py-3.5 border-b border-border">
              <div className="flex items-center gap-2.5">
                <ShoppingBag className="w-4 h-4 text-muted-foreground" />
                <h3 className="font-semibold text-foreground uppercase text-sm tracking-wide">
                  Order Summary
                </h3>
                <span className="text-xs ml-auto bg-secondary text-secondary-foreground rounded-full px-2 py-0.5">
                  {itemCount} {itemCount === 1 ? "item" : "items"}
                </span>
              </div>
            </div>

            <div className="divide-y divide-border/50">
              {ticketItems.map((item, idx) => (
                <div
                  key={`sidebar-${idx}`}
                  className="px-5 py-3 flex items-start gap-3"
                >
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-12 h-12 rounded-lg object-cover shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground line-clamp-2">
                      {item.name} × {item.count}
                    </p>
                    {item.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 flex items-start gap-1">
                        {/* Wraps rather than truncating: this line carries the
                            booking window, and the ellipsis hid the END of the
                            booking on the last screen before payment. */}
                        <Calendar className="w-3 h-3 shrink-0 mt-0.5" />
                        <span>{item.description}</span>
                      </p>
                    )}
                    {item.bookingPolicy && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePolicyClick(item.name, item.bookingPolicy);
                        }}
                        className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 font-medium mt-1 text-left transition-colors"
                      >
                        Cancellation Policy
                      </button>
                    )}
                  </div>
                  <span className="text-sm font-medium tabular-nums shrink-0">
                    {item.fullLineTotal != null ? (
                      <>
                        {formatPrice(
                          item.lineTotal ?? item.price * item.count,
                          currency,
                        )}
                        /
                        <span className="text-muted-foreground">
                          {formatPrice(item.fullLineTotal, currency)}
                        </span>
                      </>
                    ) : (
                      formatPrice(
                        item.lineTotal ?? item.price * item.count,
                        currency,
                      )
                    )}
                  </span>
                  {item.onRemove && (
                    <button
                      type="button"
                      onClick={item.onRemove}
                      className="text-muted-foreground hover:text-destructive transition-colors p-0.5 shrink-0"
                      aria-label={`Remove ${item.name}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {addonItems.map((item, idx) => (
                <div
                  key={`sidebar-addon-${idx}`}
                  className="px-5 py-2.5 flex justify-between text-sm"
                >
                  <span className="text-muted-foreground">
                    {item.name} × {item.count}
                  </span>
                  <span className="font-medium tabular-nums">
                    {formatPrice(item.price * item.count, currency)}
                  </span>
                </div>
              ))}
            </div>

            <div className="px-5 py-4 border-t border-border">
              {discountAmount === 0 &&
                (bookingFeePence > 0 || payStepServiceFeePence > 0) && (
                  <div className="flex justify-between text-sm text-muted-foreground mb-1.5">
                    <span>Subtotal</span>
                    <span className="tabular-nums">
                      {formatPrice(total, currency)}
                    </span>
                  </div>
                )}
              {discountAmount > 0 && (
                <>
                  <div className="flex justify-between text-sm text-muted-foreground mb-1.5">
                    <span>Subtotal</span>
                    <span className="tabular-nums">
                      {formatPrice(total, currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-green-600 mb-2">
                    <span className="flex items-center gap-1">
                      <Tag className="w-3.5 h-3.5" /> Discount
                    </span>
                    <span className="tabular-nums">
                      -{formatPrice(discountAmount, currency)}
                    </span>
                  </div>
                </>
              )}
              {/* Fees inside the total below: the booking fee from first paint
               *  (estimate, then the session's quote); Baluu's service fee once
               *  the session quotes it (R32). */}
              {bookingFeePence > 0 && (
                <div
                  className="flex justify-between text-sm text-muted-foreground mb-1.5"
                  title={bookingFeeDetail ?? undefined}
                >
                  <span>Booking fee</span>
                  <span className="tabular-nums">
                    {formatPrice(bookingFeePence, currency)}
                  </span>
                </div>
              )}
              {payStepServiceFeePence > 0 && (
                <div className="flex justify-between text-sm text-muted-foreground mb-1.5">
                  <span>Service fee</span>
                  <span className="tabular-nums">
                    {formatPrice(payStepServiceFeePence, currency)}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-border">
                <span className="font-semibold text-foreground">
                  {depositRemainingTotal > 0 ? "Total Deposit" : "Total"}
                </span>
                <span className="font-bold text-lg tabular-nums text-foreground">
                  {redemptionLabel ?? formatPrice(displayTotal, currency)}
                </span>
              </div>
              {depositRemainingTotal > 0 && (
                <div className="flex justify-between items-center mt-2 text-sm text-muted-foreground">
                  <span className="font-medium">Remaining balance</span>
                  <span className="font-medium tabular-nums">
                    {formatPrice(depositRemainingTotal, currency)}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mt-3">
            <Lock className="w-3 h-3" />
            <span>Secure checkout powered by Stripe</span>
          </div>
        </div>
      </div>
    );
  }, [
    orderItems,
    itemCount,
    currency,
    total,
    discountAmount,
    depositRemainingTotal,
    finalTotal,
    handlePolicyClick,
    bookingFeeDetail,
    bookingFeePence,
    payStepServiceFeePence,
    displayTotal,
    redemptionLabel,
  ]);

  // Settling a Stripe redirect's outcome
  if (returnPhase === "confirming") {
    return (
      <div className="checkout-overlay-content flex items-center justify-center bg-black/50">
        <div role="status" className="bg-background rounded-xl p-8 flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  // Success screen. Ahead of the settings gates: an order finished on the
  // way back from a Stripe redirect is confirmed even if the settings are
  // still loading or couldn't be had.
  if (isSuccess) {
    return (
      <QueryClientProvider client={queryClient}>
        <div className="checkout-overlay-content flex items-center justify-center bg-black/50 p-4">
          <div className="max-w-md w-full text-center space-y-6 bg-background rounded-2xl p-8 shadow-xl">
            <div className="relative">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            </div>

            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                {completionVariant === "requested" || completionVariant === "deposit_requested"
                  ? REQUEST_SENT_TITLE
                  : "Order Complete!"}
              </h1>
              <p className="text-muted-foreground">
                {completionVariant === "deposit_requested" && depositRequestSummary
                  ? describeDepositRequestSent(
                      depositRequestSummary.depositPence,
                      depositRequestSummary.remainingPence,
                      (p) => formatPrice(p, currency),
                      schoolConfig?.name
                    )
                  : completionVariant === "requested"
                    ? describeRequestSent(schoolConfig?.name)
                    : paymentProcessing
                      ? "Thank you for your order. Your payment is processing, and we'll email your confirmation as soon as it clears."
                      : "Thank you for your order. You will receive a confirmation email shortly."}
              </p>
            </div>

            {completionVariant !== "requested" && (
            <div className="bg-muted/50 rounded-xl border border-border p-5 text-left space-y-3">
              <h3 className="font-semibold text-foreground text-sm uppercase tracking-wide">
                What&apos;s next?
              </h3>
              <div className="space-y-2.5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Mail className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Check your email
                    </p>
                    <p className="text-xs text-muted-foreground">
                      A confirmation with all the details has been sent to your
                      inbox
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Calendar className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Save the date
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Add it to your calendar so you don&apos;t miss it
                    </p>
                  </div>
                </div>
              </div>
            </div>
            )}

            <button
              onClick={handleClose}
              className="w-full py-3 rounded-lg text-white font-medium text-sm"
              style={{ backgroundColor: primaryColor }}
            >
              Continue Browsing
            </button>
          </div>
        </div>

        {successData && (
          <SuccessScreenPortal
            isOpen={false}
            successData={successData}
            primaryColor={primaryColor}
            onClose={handleClose}
          />
        )}
      </QueryClientProvider>
    );
  }

  // Loading state while fetching config
  if (gate === "loading") {
    return (
      <div className="checkout-overlay-content flex items-center justify-center bg-black/50">
        <div className="bg-background rounded-xl p-8 flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading checkout...</p>
        </div>
      </div>
    );
  }

  // The school's settings couldn't be had: nothing to pay with until they
  // are (whether it asks for a sign-in is among them).
  if (gate === "error") {
    return (
      <div
        className="checkout-overlay-content flex items-center justify-center bg-black/50 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <div role="alert" className="bg-background rounded-xl p-8 max-w-sm w-full flex flex-col items-center gap-4 text-center">
          <p className="text-sm text-muted-foreground">We couldn&apos;t load the checkout. Please try again.</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
            <Button onClick={school.retry}>Try again</Button>
          </div>
        </div>
      </div>
    );
  }

  const ctaButtonContent = isLoading ? (
    <>
      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      Loading...
    </>
  ) : isRequestToBook ? (
    <>
      <Send className="mr-2 h-4 w-4" />
      Send request
    </>
  ) : isFreeBooking || isRedemption ? (
    "Confirm Booking"
  ) : paymentMode === "hosted" ? (
    <>
      <ExternalLink className="mr-2 h-4 w-4" />
      Continue to Payment Page
    </>
  ) : (
    <>
      <CreditCard className="mr-2 h-4 w-4" />
      Continue to Payment
    </>
  );

  return (
    <QueryClientProvider client={queryClient}>
      <div
        className="checkout-overlay-content flex items-start justify-center overflow-y-auto bg-black/50"
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <div className="relative bg-background w-full max-w-5xl mx-4 my-6 rounded-xl shadow-xl overflow-hidden">
          {/* Header. Deliberately OUTSIDE the login-gate conditional below.
              CheckoutLoginGate's contract is that it blocks the flow without
              trapping the visitor — "the overlay's close button still works" —
              and rendering it in place of the entire header broke exactly
              that: a signed-out customer got a sign-in wall with no Close
              button, leaving only a backdrop click nothing labels as an exit.
              CheckoutOrchestrator, the shared component's only other consumer,
              keeps its header rendered unconditionally and swaps just the
              content beneath it; this mirrors that.

              Everything below IS the checkout content, so it stays behind the
              gate: the availability banners describe items the visitor cannot
              act on until they sign in, and the mobile CTA calls
              handleCreateCheckoutSession, which does not itself re-check the
              gate — rendering it alongside the gate would hand out a working
              Pay button to a customer the school requires to be signed in. */}
          <header className="sticky top-0 z-10 bg-background border-b shadow-sm">
            <div className="px-4 sm:px-6 py-3 flex items-center justify-between">
              <button
                onClick={handleClose}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline">Close</span>
              </button>
              <h1 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
                <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5" />
                Checkout
                <span className="text-xs bg-secondary text-secondary-foreground rounded-full px-2 py-0.5">
                  {itemCount} {itemCount === 1 ? "item" : "items"}
                </span>
              </h1>
              <div className="w-12 sm:w-16" />
            </div>
            {/* Progress bar */}
            <div className="flex gap-1.5 px-4 pb-2">
              <div className="h-1 flex-1 rounded-full bg-primary transition-all duration-500" />
              <div
                className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                  isPaymentReady ? "bg-primary" : "bg-border"
                }`}
              />
            </div>
          </header>

          {showLoginGate ? (
            <CheckoutLoginGate
              schoolName={schoolConfig?.name}
              onLogin={openLogin}
            />
          ) : (
            <>
              {/* Validation banner */}
              {isValidating && (
                <div className="px-4 sm:px-6 py-2 bg-muted/50 border-b flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Checking availability...
                  </span>
                </div>
              )}

              {/* Routes the buyer to the drawer rather than restating the problem
              here. The per-item reason ("Only 2 spots left", "Sold out", the
              equipment wording) is rendered by BasketItemStatus over there,
              along with whatever fix that verdict has; this overlay has no
              per-item UI to show it in. The old copy asserted every flagged
              item was "no longer available" and that removal was the fix,
              which was wrong for most buckets. */}
              {/* Not once the card form is up. StripePaymentPortal renders through
              a portal to document.body at z-index 10000 over this overlay's
              9999, with a 50%-opaque backdrop — so from the payment step on,
              this banner is dimmed-but-legible and completely unclickable. A
              cross-tab drawer validate (the store syncs on `storage`) can
              stamp a blocker mid-payment, and it is a FALSE one by
              construction: that validate is anonymous, so the server counted
              this buyer's own reservation against them. Telling someone their
              basket "needs attention" over a live payment intent they cannot
              act on is worse than silence, and the server re-validated when it
              created the session. */}
              {showNeedsAttention && (
                <div className="px-4 sm:px-6 py-3 bg-destructive/10 border-b border-destructive/20 flex items-start gap-3">
                  <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-destructive">
                      {blockingItemCount === 1
                        ? "1 item in your basket needs attention"
                        : `${blockingItemCount} items in your basket need attention`}
                    </p>
                    <p className="text-xs text-destructive/80 mt-0.5">
                      {identityBlockCopy
                        ? identityBlockCopy
                        : !canReviewBasket
                          ? "We can't take payment for it as it stands."
                          : hasStartedForm
                            ? "Open your basket to see why and fix it. You'll need to re-enter your details afterwards."
                            : "Open your basket to see why and fix it before paying."}
                    </p>
                  </div>
                  {canReviewBasket && !blockedOnIdentity && reviewBasketButton}
                </div>
              )}

              {/* Main content */}
              <main className="px-4 sm:px-6 py-6">
                <div className="lg:grid lg:grid-cols-[1fr_380px] lg:gap-8">
                  <div className="space-y-5 pb-36 lg:pb-6">
                    {/* Checkout form with FinalStep */}
                    {isPaymentReady && stripePaymentData ? (
                      <FinalStep
                        items={orderItems}
                        total={displayTotal}
                        bookingFeePence={bookingFeePence}
                        serviceFeePence={payStepServiceFeePence}
                        depositTotals={
                          depositRemainingTotal > 0
                            ? { remainingBalance: depositRemainingTotal }
                            : undefined
                        }
                        currency={currency}
                        schoolId={businessId}
                        discount={discountAmount}
                        discountPercentage={discountPercentage}
                        discountCode={singleDiscountCode}
                        isDiscountApplied={!allowStacking && appliedDiscounts.length > 0}
                        isDiscountEnabled={!allowStacking}
                        onApplyDiscount={handleApplyDiscount}
                        onRemoveDiscount={handleRemoveDiscount}
                        paymentMode="embedded"
                        isFreeBooking={isFreeBooking}
                        isExternalPortalMode={true}
                        isLoading={false}
                        isSubmitting={isSubmitting}
                        cancellationPolicyUrl={
                          schoolConfig?.cancellationPolicyUrl
                        }
                        termsSettings={schoolConfig?.termsSettings}
                        customTerms={schoolConfig?.customTerms}
                        timezone={timezone}
                        onOpenTermsDialog={handleOpenTermsDialog}
                        onPolicyClick={handlePolicyClick}
                        beforeOrderSummary={beforeOrderSummaryContent}
                        bookingFeeDetail={bookingFeeDetail}
                        hideInlineOrderSummary={true}
                        className="lg:max-w-none"
                      />
                    ) : (
                      <>
                        <FinalStep
                          items={orderItems}
                          total={displayTotal}
                          bookingFeePence={bookingFeePence}
                          serviceFeePence={payStepServiceFeePence}
                          depositTotals={
                            depositRemainingTotal > 0
                              ? { remainingBalance: depositRemainingTotal }
                              : undefined
                          }
                          currency={currency}
                          schoolId={businessId}
                          discount={discountAmount}
                          discountPercentage={discountPercentage}
                          discountCode={singleDiscountCode}
                          isDiscountApplied={!allowStacking && appliedDiscounts.length > 0}
                          isDiscountEnabled={!allowStacking}
                          onApplyDiscount={handleApplyDiscount}
                          onRemoveDiscount={handleRemoveDiscount}
                          paymentMode={paymentMode}
                          isFreeBooking={isFreeBooking}
                          isExternalPortalMode={true}
                          isLoading={false}
                          isSubmitting={false}
                          cancellationPolicyUrl={
                            schoolConfig?.cancellationPolicyUrl
                          }
                          termsSettings={schoolConfig?.termsSettings}
                          customTerms={schoolConfig?.customTerms}
                          timezone={timezone}
                          onOpenTermsDialog={handleOpenTermsDialog}
                          onPolicyClick={handlePolicyClick}
                          beforeOrderSummary={beforeOrderSummaryContent}
                          bookingFeeDetail={bookingFeeDetail}
                          // Pay with an owned bundle or membership credits (the
                          // widget checkout's options). Signed out: a prompt to
                          // sign in for them. A request takes no payment, so
                          // none of it is offered there.
                          userApplicableBundles={
                            isRequestToBook || !isLoggedIn ? undefined : eligibleBundles
                          }
                          selectedPaymentBundleId={liveBundleId ?? undefined}
                          onSelectPaymentBundle={(id) => {
                            setSelectedPaymentBundleId(id);
                            setSelectedMembershipId(null);
                          }}
                          onDeselectPaymentBundle={() => setSelectedPaymentBundleId(null)}
                          membershipResolution={
                            isRequestToBook ? undefined : membershipResolution ?? undefined
                          }
                          selectedMembershipId={liveMembershipId}
                          onSelectMembership={(id) => {
                            setSelectedMembershipId(id);
                            setSelectedPaymentBundleId(null);
                          }}
                          onDeselectMembership={() => setSelectedMembershipId(null)}
                          onPaymentOptionChange={setSelectedPaymentOption}
                          onLogin={!isLoggedIn && !isRequestToBook ? openLogin : undefined}
                          className="lg:max-w-none"
                        />

                        {/* Desktop CTA */}
                        <button
                          onClick={handleCreateCheckoutSession}
                          disabled={isLoading || !!paymentPickError}
                          className="w-full hidden lg:flex items-center justify-center gap-2 py-3 rounded-lg text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {ctaButtonContent}
                        </button>
                        {isRequestToBook && (
                          <p className="hidden lg:block text-xs text-muted-foreground text-center">
                            {describeRequestNoPayment(schoolConfig?.name)}
                          </p>
                        )}
                        {depositAtRequestNote && (
                          <p className="hidden lg:block text-xs text-muted-foreground text-center">
                            {depositAtRequestNote}
                          </p>
                        )}
                      </>
                    )}

                    {/* Error display. A refusal of the basket's contents names
                    the fix on this screen: the order summary's remove × (both
                    breakpoints, and the buyer keeps what they've typed), plus
                    the drawer when this page has one. */}
                    {errorSlot.error && (
                      <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-destructive">
                            {errorSlot.error}
                          </p>
                          {errorSlot.basketFix && (
                            <p className="text-xs text-destructive/80 mt-1">
                              {REMOVE_FROM_SUMMARY_HINT}
                            </p>
                          )}
                        </div>
                        {refusalOffersBasket && reviewBasketButton}
                        <button
                          type="button"
                          onClick={() => {
                            // Dismissing takes the message off screen, so the flag
                            // describing it stops being true here too - otherwise
                            // it survives to retract a later, unrelated error.
                            serverRefusedOnAvailabilityRef.current = false;
                            setContentsRefusal(null);
                            setError(null);
                          }}
                          className="p-1 rounded-full text-destructive/60 hover:text-destructive transition-colors shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Desktop sidebar */}
                  {orderSummarySidebar}
                </div>
              </main>

              {/* Mobile sticky CTA footer */}
              {!isPaymentReady && (
                <div className="fixed bottom-0 left-0 right-0 z-30 bg-background/95 backdrop-blur-sm border-t border-border p-4 lg:hidden">
                  <div className="max-w-2xl mx-auto">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">
                        {isRequestToBook
                          ? requestToBookTotalLabel(depositRemainingTotal > 0)
                          : depositRemainingTotal > 0
                            ? "Total Deposit"
                            : "Total"}
                      </span>
                      <span className="font-bold text-lg tabular-nums">
                        {redemptionLabel ?? formatPrice(displayTotal, currency)}
                      </span>
                    </div>
                    {bookingFeePence > 0 && (
                      <p
                        className="text-xs text-muted-foreground mb-2 text-right"
                        title={bookingFeeDetail ?? undefined}
                      >
                        Includes {formatPrice(bookingFeePence, currency)} booking fee
                        {bookingFeeDetail && <span className="sr-only"> {bookingFeeDetail}</span>}
                      </p>
                    )}
                    {depositRemainingTotal > 0 && (
                      <div className="flex items-center justify-between mb-2 text-xs text-muted-foreground">
                        <span>Remaining balance</span>
                        <span className="tabular-nums">
                          {formatPrice(depositRemainingTotal, currency)}
                        </span>
                      </div>
                    )}
                    <button
                      onClick={handleCreateCheckoutSession}
                      disabled={isLoading || !!paymentPickError}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {ctaButtonContent}
                    </button>
                    {isRequestToBook && (
                      <p className="mt-2 text-xs text-muted-foreground text-center">
                        {describeRequestNoPayment(schoolConfig?.name)}
                      </p>
                    )}
                    {depositAtRequestNote && (
                      <p className="mt-2 text-xs text-muted-foreground text-center">
                        {depositAtRequestNote}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Customer sign-in dialog, rendered in Shadow DOM portal for proper styling */}
      <CustomerAuthDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        apiUrl={apiUrl}
        businessSlug={schoolConfig?.slug}
        schoolName={schoolConfig?.name}
        primaryColor={primaryColor}
      />

      {/* Stripe portal (outside Shadow DOM) */}
      {stripePaymentData && (
        <StripePaymentPortal
          isOpen={!!clientSecret}
          paymentData={stripePaymentData}
          stripePublicKey={stripePaymentData.stripePublicKey || ""}
          primaryColor={primaryColor}
          returnUrl={stripeReturnUrl ?? undefined}
          onSuccess={handleStripeSuccess}
          onClose={handleStripeClose}
        />
      )}

      {/* Terms dialog portal (outside Shadow DOM) */}
      <TermsDialogPortal
        isOpen={termsDialogData.isOpen}
        onClose={handleCloseTermsDialog}
        settings={termsDialogData.termsSettings}
        customTerms={termsDialogData.customTerms}
        primaryColor={primaryColor}
        ticketPolicies={termsDialogData.ticketPolicies}
      />
    </QueryClientProvider>
  );
}

function dispatchCompletedEvent(
  basketOrderId: string,
  total: number,
  itemCount: number,
) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("baluu:basket-checkout-completed", {
      detail: { basketOrderId, total, itemCount },
    }),
  );
}
